import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ElementType, type PointerEvent as ReactPointerEvent } from "react";
import { HugeiconsIcon, type HugeiconsIconProps, type IconSvgElement } from "@hugeicons/react";
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  ArrowUpRight01Icon,
  CloudIcon,
  CodeIcon,
  CreditCardIcon,
  CpuIcon,
  CubeIcon,
  GameController03Icon,
  HeartPulseIcon,
  Home01Icon,
  Radio01Icon,
  Search01Icon,
  SecurityCheckIcon,
  SparklesIcon,
} from "@hugeicons/core-free-icons";
import {
  SiApple,
  SiCloudflare,
  SiDocker,
  SiLua,
  SiNextdotjs,
  SiNodedotjs,
  SiPostgresql,
  SiReact,
  SiRedis,
  SiRoblox,
  SiStripe,
  SiSwift,
  SiTailwindcss,
  SiThreedotjs,
  SiTypescript,
  SiJavascript,
  SiFlutter,
  SiLaravel,
  SiMysql,
  SiPhp,
  SiWoocommerce,
  SiWordpress,
} from "react-icons/si";
import { getCaseStudy } from "./caseStudies";
import { projects, type Project } from "./projects";

type StackIcon = ElementType;

function createHugeIcon(icon: IconSvgElement) {
  return function PortfolioHugeIcon(props: Omit<HugeiconsIconProps, "icon">) {
    return <HugeiconsIcon icon={icon} {...props} />;
  };
}

const ArrowLeft = createHugeIcon(ArrowLeft01Icon);
const ArrowRight = createHugeIcon(ArrowRight01Icon);
const ArrowUpRight = createHugeIcon(ArrowUpRight01Icon);
const Box = createHugeIcon(CubeIcon);
const Cloud = createHugeIcon(CloudIcon);
const Code2 = createHugeIcon(CodeIcon);
const CreditCard = createHugeIcon(CreditCardIcon);
const Cpu = createHugeIcon(CpuIcon);
const Gamepad2 = createHugeIcon(GameController03Icon);
const HeartPulse = createHugeIcon(HeartPulseIcon);
const HomeIcon = createHugeIcon(Home01Icon);
const Radio = createHugeIcon(Radio01Icon);
const Search = createHugeIcon(Search01Icon);
const ShieldCheck = createHugeIcon(SecurityCheckIcon);
const Sparkles = createHugeIcon(SparklesIcon);

const stackIcons: Record<string, StackIcon> = {
  "Next.js": SiNextdotjs,
  TypeScript: SiTypescript,
  PostgreSQL: SiPostgresql,
  Stripe: SiStripe,
  React: SiReact,
  OpenAI: Sparkles,
  Cloudflare: SiCloudflare,
  "Node.js": SiNodedotjs,
  Roblox: SiRoblox,
  Lua: SiLua,
  "Three.js": SiThreedotjs,
  Docker: SiDocker,
  Swift: SiSwift,
  SwiftUI: SiApple,
  HealthKit: HeartPulse,
  StoreKit: CreditCard,
  Apple: SiApple,
  CloudKit: Cloud,
  Security: ShieldCheck,
  "Game Design": Gamepad2,
  "Game Center": Gamepad2,
  SpriteKit: Sparkles,
  "3D": Box,
  Realtime: Radio,
  JavaScript: SiJavascript,
  WordPress: SiWordpress,
  PHP: SiPhp,
  MySQL: SiMysql,
  Redis: SiRedis,
  WooCommerce: SiWoocommerce,
  "Mercado Pago": CreditCard,
  Printify: Box,
  Laravel: SiLaravel,
  Flutter: SiFlutter,
  "Tailwind CSS": SiTailwindcss,
  Metal: Cpu,
  Playwright: Code2,
  Satori: Sparkles,
};

const letters = Array.from(new Set(projects.map((project) => project.domain[0].toUpperCase())));
const alphabetEntries = ["home", ...letters];
const mediaRestoreVersion = "restored-20260720-v2";

function mediaAssetUrl(src: string) {
  if (!src.startsWith("/media/")) return src;
  return `${src}${src.includes("?") ? "&" : "?"}restore=${mediaRestoreVersion}`;
}

const homePress = [
  {
    outlet: "SYFY",
    title: "Geek road trip in Buenos Aires",
    href: "https://www.syfy.com/syfy-wire/geek-road-trip-in-buenos-aires-bars-and-burger-joints-are-bringing-retro-games-back",
    logo: "/media/home/press/syfy.svg",
  },
  {
    outlet: "Dispatches Europe",
    title: "New restaurants and cafés in Lisbon",
    href: "https://dispatcheseurope.com/monica-da-silva-new-restaurants-and-cafes-in-lisbon-that-are-must-tries/",
    logo: "/media/home/press/dispatches-europe.png",
  },
  {
    outlet: "Infobae",
    title: "Burgermanía in Buenos Aires",
    href: "https://www.infobae.com/tendencias/2019/12/09/burgermania-16-hamburguesas-gourmet-para-disfrutar-en-buenos-aires/",
    logo: "/media/home/press/infobae.svg",
  },
  {
    outlet: "iProUP",
    title: "Burgertify entrepreneurship and franchise",
    href: "https://www.iproup.com/startups/37536-ideas-emprendimientos-cuanto-sale-la-franquicia-de-burgertify",
    logo: "/media/home/press/iproup.png",
  },
] as const;

const homeProjectFilters = [
  { id: "all", label: "All", matches: () => true },
  { id: "ai", label: "AI", matches: (project: Project) => /\bai\b|openai|deepseek|gemini/i.test([project.category, project.description, ...project.stack, ...(project.productTypes ?? [])].join(" ")) },
  { id: "ios", label: "iOS", matches: (project: Project) => projectTaxonomy(project).platforms.includes("iOS") },
  { id: "web", label: "Web", matches: (project: Project) => projectTaxonomy(project).platforms.some((platform) => /web/i.test(platform)) },
  { id: "games", label: "Games", matches: (project: Project) => /game|arcade|roblox|spritekit|metal/i.test([project.category, ...project.stack, ...(project.productTypes ?? [])].join(" ")) },
  { id: "education", label: "Education", matches: (project: Project) => /education|course|learning|school/i.test([project.category, project.description, ...(project.productTypes ?? [])].join(" ")) },
  { id: "commerce", label: "Commerce", matches: (project: Project) => /commerce|stripe|woocommerce|mercado pago|printify/i.test([project.category, project.description, ...project.stack, ...(project.productTypes ?? [])].join(" ")) },
] as const;

const portugueseProjects = new Set([
  "aulasdeia.com", "antesdepois.com", "artmuseumbrasil.com", "fotoprofissa.com",
  "jogaia.com", "jornaldaia.com", "logoahora.com", "orcanahora.com", "qrloja.com",
  "radardeia.com", "vendakit.com",
]);

const spanishProjects = new Set([
  "aiclases.com", "cursalo.com", "developargentina.com", "iaespacio.com",
  "iglesiajesus.com", "linkmas.com", "manualdelrestaurante.com", "matrixagencia.com",
  "restaurantargentina.com",
]);

function projectTaxonomy(project: Project) {
  const platforms = project.platforms ?? (
    project.stack.includes("Swift")
      ? ["iOS"]
      : project.stack.includes("Roblox")
        ? ["Roblox"]
        : ["Web App"]
  );

  const inferredTypes = [project.category];
  if ((project.stack.includes("OpenAI") || /\bAI\b/i.test(project.category)) && !/AI/i.test(project.category)) {
    inferredTypes.push("AI");
  }
  if (project.stack.includes("Stripe") && /(commerce|sales|restaurant|course|education)/i.test(project.category)) {
    inferredTypes.push("Commerce");
  }

  const languages = project.languages ?? (
    portugueseProjects.has(project.domain)
      ? ["PT-BR"]
      : spanishProjects.has(project.domain)
        ? ["ES"]
        : ["EN"]
  );

  return {
    platforms,
    productTypes: project.productTypes ?? Array.from(new Set(inferredTypes)).slice(0, 2),
    languages,
  };
}

function ProjectTaxonomy({ project }: { project: Project }) {
  const taxonomy = projectTaxonomy(project);
  const groups = [
    ["Platform", taxonomy.platforms],
    ["Type", taxonomy.productTypes],
    ["Language", taxonomy.languages],
  ] as const;

  return (
    <div className="project-taxonomy" aria-label="Product classification">
      {groups.map(([label, values]) => (
        <div className="taxonomy-group" key={label}>
          <span className="taxonomy-label">{label}</span>
          <div className="taxonomy-values">
            {values.map((value) => <span className="taxonomy-chip" key={value}>{value}</span>)}
          </div>
        </div>
      ))}
    </div>
  );
}

function projectWebsiteUrl(project: Project) {
  return project.websiteUrl === null ? null : (project.websiteUrl ?? `https://${project.domain}`);
}

function projectSurfaceLabel(project: Project) {
  return project.displayDomain ?? project.domain;
}

function projectSlug(project: Project) {
  return project.domain.replace(/\.[^.]+$/, "").replace(/[^a-z0-9]+/g, "-").toLowerCase();
}

function projectCode(project: Project, index: number) {
  const letter = project.domain[0].toUpperCase();
  const position = projects.slice(0, index + 1).filter((item) => item.domain[0] === project.domain[0]).length;
  return `${letter}.${String(position).padStart(2, "0")}`;
}

function MediaDeck({ project, index }: { project: Project; index: number }) {
  if (project.domain === "agrofounder.com") {
    return <AgroFounderMediaDeck />;
  }
  if (project.domain === "astracam") {
    return <AstraCAMMediaDeck />;
  }
  if (project.domain === "australleather.com") {
    return <AustralLeatherMediaDeck />;
  }
  if (project.domain === "antesdepois.com") {
    return <AntesDepoisMediaDeck />;
  }
  if (project.domain === "aulasdeia.com") {
    return <AulasDeIAMediaDeck />;
  }
  if (project.domain === "artmuseumbrasil.com") {
    return <ArtMuseumMediaDeck />;
  }
  if (project.domain === "eaxy.ai") {
    return <EaxyMediaDeck />;
  }
  if (project.domain === "huntorizon.com") {
    return <HuntorizonMediaDeck />;
  }
  if (project.domain === "minibicho.com") {
    return <MiniBichoMediaDeck />;
  }
  if (project.domain === "skullsailor.com") {
    return <SkullSailorMediaDeck />;
  }
  if (project.domain === "aiclases.com") {
    return <AIClasesMediaDeck />;
  }
  if (project.domain === "avatarmatic.com") {
    return <AvatarmaticMediaDeck />;
  }
  if (project.domain === "callturbo.com") {
    return <CallTurboMediaDeck />;
  }
  if (project.domain === "roomrenovation.ai") {
    return <RoomRenovationMediaDeck />;
  }
  if (project.domain === "foodphoto.ai") {
    return <FoodPhotoMediaDeck />;
  }
  if (project.domain === "fotoprofissa.com") {
    return <FotoProfissaMediaDeck />;
  }
  if (project.domain === "cursalo.com") {
    return <CursaloMediaDeck />;
  }
  if (project.domain === "kublaro.com") {
    return <KublaroMediaDeck />;
  }
  if (project.domain === "developargentina.com") {
    return <DevelopArgentinaMediaDeck />;
  }
  if (project.domain === "restaurantargentina.com") {
    return <RestaurantArgentinaMediaDeck />;
  }
  if (project.domain === "restaurantmargin.com") {
    return <RestaurantMarginMediaDeck />;
  }
  if (project.domain === "eateasier.com") {
    return <EatEasierMediaDeck />;
  }
  if (project.domain === "jornaldaia.com") {
    return <JornalDaIAMediaDeck />;
  }
  if (project.domain === "radardeia.com") {
    return <RadarDeIAMediaDeck />;
  }
  if (project.domain === "nollei.com") {
    return <NolleiMediaDeck />;
  }
  if (project.domain === "codeaustral.com") {
    return <CodeAustralMediaDeck />;
  }
  if (project.domain === "linkmas.com") {
    return <LinkMasMediaDeck />;
  }
  if (project.domain === "genetiqo.com") {
    return <GenetiqoMediaDeck />;
  }
  if (project.domain === "foodelopers.com") {
    return <FoodelopersMediaDeck />;
  }
  if (project.domain === "bunbandit.com") {
    return <BunBanditMediaDeck />;
  }
  if (project.domain === "rockclash.com") {
    return <RockClashMediaDeck />;
  }
  if (project.domain === "makobits.com") {
    return <MakoBitsMediaDeck />;
  }
  if (project.domain === "jogaia.com") {
    return <JogaIAMediaDeck />;
  }
  if (project.domain === "talkmaestro.com") {
    return <TalkMaestroMediaDeck />;
  }
  if (project.domain === "iaespacio.com") {
    return <IAEspacioMediaDeck />;
  }
  if (project.domain === "iglesiajesus.com") {
    return <IglesiaJesusMediaDeck />;
  }
  if (project.domain === "logoahora.com") {
    return <LogoAhoraMediaDeck />;
  }
  if (project.domain === "matrixagencia.com") {
    return <MatrixAgenciaMediaDeck />;
  }
  if (project.domain === "youareearly.com") {
    return <YouAreEarlyMediaDeck />;
  }
  if (project.domain === "menucrafters.com") {
    return <MenuCraftersMediaDeck />;
  }
  if (project.domain === "xagod.com") {
    return <XagodMediaDeck />;
  }
  if (project.domain === "postraptor.com") {
    return <PostRaptorMediaDeck />;
  }
  if (project.domain === "pitchdeckgenie.com") {
    return <PitchDeckGenieMediaDeck />;
  }
  if (project.domain === "vendakit.com") {
    return <VendaKitMediaDeck />;
  }
  if (project.domain === "propbotstudio.com") {
    return <PropBotStudioMediaDeck />;
  }
  if (project.domain === "qrloja.com") {
    return <QRLojaMediaDeck />;
  }
  if (project.domain === "manualdelrestaurante.com") {
    return <ManualDelRestauranteMediaDeck />;
  }
  if (project.domain === "orcanahora.com") {
    return <OrcaNaHoraMediaDeck />;
  }
  if (project.domain === "uglyprofitable.com") {
    return <UglyProfitableMediaDeck />;
  }
  if (project.domain === "takeaicourse.com") {
    return <TakeAICourseMediaDeck />;
  }
  if (project.domain === "zerotopublic.com") {
    return <ZeroToPublicMediaDeck />;
  }

  const shortDomain = project.domain.replace(/\.(com|ai)$/i, "");

  return (
    <div className="media-deck" aria-label={`Future media preview for ${project.title}`}>
      {["system", "motion", "detail"].map((view, cardIndex) => (
        <div
          className={`media-card media-card-${cardIndex + 1}`}
          key={view}
          style={{ "--card-hue": `${(project.hue + cardIndex * 28) % 360}` } as CSSProperties}
        >
          <div className="media-card-bar">
            <span /><span /><span />
            <small>{view}</small>
          </div>
          <div className="media-card-visual">
            <div className="kinetic-orbit orbit-one" />
            <div className="kinetic-orbit orbit-two" />
            <span className="media-index">{String(index + 1).padStart(2, "0")}</span>
            <strong>{shortDomain}</strong>
            <small>case study media / coming next</small>
          </div>
        </div>
      ))}
      <div className="deck-hint"><span /> Hover to activate</div>
    </div>
  );
}

const antesDepoisMedia = [
  {
    label: "Mobile visual decision and comparison flow",
    poster: "/media/antesdepois/mobile-visual-decision-v1.jpg?v=2",
    video: "/media/antesdepois/mobile-visual-decision-v1.mp4?v=2",
    format: "ios",
  },
  {
    label: "Interactive before-and-after proof system",
    poster: "/media/antesdepois/comparison-proof-v1.jpg?v=2",
    video: "/media/antesdepois/comparison-proof-v1.mp4?v=2",
    format: "wide",
  },
  {
    label: "One hundred ready visual directions",
    poster: "/media/antesdepois/preset-library-v1.jpg?v=2",
    video: "/media/antesdepois/preset-library-v1.mp4?v=2",
    format: "wide",
  },
] as const;

const artMuseumMedia = [
  {
    label: "iOS walk through the warm brown Sala Trabalho",
    poster: "/media/artmuseumbrasil/ios-trabalho.jpg",
    video: "/media/artmuseumbrasil/ios-trabalho.mp4",
    format: "ios",
  },
  {
    label: "Web camera tour through Sala Cidade",
    poster: "/media/artmuseumbrasil/web-cidade.jpg",
    video: "/media/artmuseumbrasil/web-cidade.mp4",
    format: "web",
  },
  {
    label: "Artwork interface in Sala Retratos",
    poster: "/media/artmuseumbrasil/artwork-detail.jpg",
    video: "/media/artmuseumbrasil/artwork-detail.mp4",
    format: "detail",
  },
] as const;

const agroFounderMedia = [
  {
    label: "Interactive Brazilian production map responding to real state navigation",
    poster: "/media/agrofounder/map-real.jpg?v=3",
    video: "/media/agrofounder/map-real.mp4?v=3",
    format: "wide",
  },
  {
    label: "Real AgroFounder assistant answering from farm data and cited evidence",
    poster: "/media/agrofounder/chat-real.jpg?v=2",
    video: "/media/agrofounder/chat-real.mp4?v=2",
    format: "wide",
  },
  {
    label: "Real tour through operations, exceptions, approvals, stock, sensors and devices",
    poster: "/media/agrofounder/modules-real.jpg?v=2",
    video: "/media/agrofounder/modules-real.mp4?v=2",
    format: "wide",
  },
] as const;

const astraCAMMedia = [
  {
    label: "Native AstraCAM capture cockpit and focused module navigation",
    poster: "/media/astracam/capture-modules-real-v2.jpg?v=2",
    video: "/media/astracam/capture-modules-real-v2.mp4?v=2",
    format: "ios",
  },
  {
    label: "Real lunar, First Light and night-planning workflows",
    poster: "/media/astracam/field-planning-real.jpg?v=1",
    video: "/media/astracam/field-planning-real.mp4?v=1",
    format: "ios",
  },
  {
    label: "Real processing, Album and field-journal handoff",
    poster: "/media/astracam/album-journal-real.jpg?v=1",
    video: "/media/astracam/album-journal-real.mp4?v=1",
    format: "ios",
  },
] as const;

const australLeatherMedia = [
  {
    label: "Austral Leather wholesale export platform in motion",
    poster: "/media/australleather/home-real.jpg?v=2",
    video: "/media/australleather/home-real.mp4?v=2",
    format: "wide",
  },
  {
    label: "Real material library and specification-led sampling workflow",
    poster: "/media/australleather/materials-real.jpg?v=2",
    video: "/media/australleather/materials-real.mp4?v=2",
    format: "wide",
  },
  {
    label: "Real reference-color library for wholesale leather programs",
    poster: "/media/australleather/colors-real.jpg?v=2",
    video: "/media/australleather/colors-real.mp4?v=2",
    format: "wide",
  },
] as const;

const eaxyMedia = [
  {
    label: "Eaxy product entrance and connected AI workspace",
    poster: "/media/eaxy/studio-overview-v3.jpg?v=3",
    video: "/media/eaxy/studio-overview-v3.mp4?v=3",
    format: "wide",
  },
  {
    label: "Practical workflow system and chat-agent product",
    poster: "/media/eaxy/feature-system-v3.jpg?v=3",
    video: "/media/eaxy/feature-system-v3.mp4?v=3",
    format: "wide",
  },
  {
    label: "Image-generation product and visual result gallery",
    poster: "/media/eaxy/image-generation-v4.jpg?v=4",
    video: "/media/eaxy/image-generation-v4.mp4?v=4",
    format: "wide",
  },
] as const;

const huntorizonMedia = [
  {
    label: "Level 01 · Moonroot Forest — native movement and ranged combat",
    poster: "/media/huntorizon/moonroot-forest-v4.jpg?v=4",
    video: "/media/huntorizon/moonroot-forest-v4.mp4?v=4",
    format: "ios",
  },
  {
    label: "Level 02 · Hollow Keep — cathedral movement and ranged combat",
    poster: "/media/huntorizon/hollow-keep-v4.jpg?v=4",
    video: "/media/huntorizon/hollow-keep-v4.mp4?v=4",
    format: "ios",
  },
  {
    label: "Level 20 · Eclipse Throne — eclipse arena movement and live damage",
    poster: "/media/huntorizon/eclipse-throne-v4.jpg?v=4",
    video: "/media/huntorizon/eclipse-throne-v4.mp4?v=4",
    format: "ios",
  },
] as const;

const miniBichoMedia = [
  {
    label: "Native 9:16 care interaction with the Capivara",
    poster: "/media/minibicho/pet-care-v3.jpg?v=3",
    video: "/media/minibicho/pet-care-v3.mp4?v=3",
    format: "ios",
  },
  {
    label: "MiniBicho encyclopedia, species collection and detail",
    poster: "/media/minibicho/encyclopedia-v3.jpg?v=3",
    video: "/media/minibicho/encyclopedia-v3.mp4?v=3",
    format: "ios",
  },
  {
    label: "Frutaverso gameplay with live placements and score changes",
    poster: "/media/minibicho/frutaverso-v3.jpg?v=3",
    video: "/media/minibicho/frutaverso-v3.mp4?v=3",
    format: "ios",
  },
] as const;

const skullSailorMedia = [
  {
    label: "Native horizontal voyage briefing and campaign entry",
    poster: "/media/skullsailor/briefing-v3.jpg?v=3",
    video: "/media/skullsailor/briefing-v3.mp4?v=3",
    format: "wide",
  },
  {
    label: "Full-sail horizontal navigation away from the coast",
    poster: "/media/skullsailor/full-sail-v3.jpg?v=3",
    video: "/media/skullsailor/full-sail-v3.mp4?v=3",
    format: "wide",
  },
  {
    label: "Horizontal cannon encounter with live heat and enemy state",
    poster: "/media/skullsailor/cannon-combat-v3.jpg?v=3",
    video: "/media/skullsailor/cannon-combat-v3.mp4?v=3",
    format: "wide",
  },
] as const;

const aiClasesMedia = [
  {
    label: "Responsive course experience",
    poster: "/media/aiclases/course-mobile-v2.jpg?v=3",
    video: "/media/aiclases/course-mobile-v2.mp4?v=3",
    format: "ios",
  },
  {
    label: "AIClases learning platform homepage",
    poster: "/media/aiclases/home-v2.jpg?v=3",
    video: "/media/aiclases/home-v2.mp4?v=3",
    format: "wide",
  },
  {
    label: "25-course AI catalog",
    poster: "/media/aiclases/catalog-v2.jpg?v=3",
    video: "/media/aiclases/catalog-v2.mp4?v=3",
    format: "wide",
  },
] as const;

const aulasDeIAMedia = [
  {
    label: "Responsive dark course experience",
    poster: "/media/aulasdeia/course-mobile-v1.jpg?v=2",
    video: "/media/aulasdeia/course-mobile-v1.mp4?v=2",
    format: "ios",
  },
  {
    label: "AulasDeIA dark learning platform",
    poster: "/media/aulasdeia/home-v1.jpg?v=2",
    video: "/media/aulasdeia/home-v1.mp4?v=2",
    format: "wide",
  },
  {
    label: "Portuguese AI course catalog",
    poster: "/media/aulasdeia/catalog-v1.jpg?v=2",
    video: "/media/aulasdeia/catalog-v1.mp4?v=2",
    format: "wide",
  },
] as const;

const roomRenovationMedia = [
  {
    label: "Native iOS Studio and style selection",
    poster: "/media/roomrenovation/ios-studio-v1.jpg",
    video: "/media/roomrenovation/ios-studio-v1.mp4",
    format: "ios",
  },
  {
    label: "Room-aware renovation workflow on the web",
    poster: "/media/roomrenovation/web-studio-v1.jpg",
    video: "/media/roomrenovation/web-studio-v1.mp4",
    format: "wide",
  },
  {
    label: "Live renovation plans and rendering tiers",
    poster: "/media/roomrenovation/web-plans-v1.jpg",
    video: "/media/roomrenovation/web-plans-v1.mp4",
    format: "wide",
  },
] as const;

const foodPhotoMedia = [
  {
    label: "FoodPhoto 2.0 native iOS Studio, Styles and Layers",
    poster: "/media/foodphoto/ios-v2-studio.jpg",
    video: "/media/foodphoto/ios-v2-studio.mp4",
    format: "ios",
  },
  {
    label: "Complete FoodPhoto web dashboard and creation tools",
    poster: "/media/foodphoto/web-dashboard-v1.jpg",
    video: "/media/foodphoto/web-dashboard-v1.mp4",
    format: "wide",
  },
  {
    label: "FoodPhoto restaurant photography presets",
    poster: "/media/foodphoto/web-presets-v1.jpg",
    video: "/media/foodphoto/web-presets-v1.mp4",
    format: "wide",
  },
] as const;

const fotoProfissaMedia = [
  {
    label: "FotoProfissa mobile transformation journey",
    poster: "/media/fotoprofissa/mobile-transformations-v1.jpg?v=1",
    video: "/media/fotoprofissa/mobile-transformations-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Natural before-and-after portrait library",
    poster: "/media/fotoprofissa/transformation-library-v1.jpg?v=1",
    video: "/media/fotoprofissa/transformation-library-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Portrait services, formats and PIX packages",
    poster: "/media/fotoprofissa/services-pricing-v1.jpg?v=1",
    video: "/media/fotoprofissa/services-pricing-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const cursaloMedia = [
  {
    label: "Cursalo mobile discovery and course catalog",
    poster: "/media/cursalo/mobile-learning-v1.jpg?v=1",
    video: "/media/cursalo/mobile-learning-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "One hundred forty-one courses organized by skill",
    poster: "/media/cursalo/course-catalog-v1.jpg?v=1",
    video: "/media/cursalo/course-catalog-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Complete course page, curriculum and instructor system",
    poster: "/media/cursalo/course-experience-v1.jpg?v=1",
    video: "/media/cursalo/course-experience-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const kublaroMedia = [
  {
    label: "Kublaro generative image and video mural",
    poster: "/media/kublaro/home-v1.jpg",
    video: "/media/kublaro/home-v1.mp4",
    format: "wide",
  },
  {
    label: "Kublaro creation workflow library",
    poster: "/media/kublaro/create-v1.jpg",
    video: "/media/kublaro/create-v1.mp4",
    format: "wide",
  },
  {
    label: "Kublaro Smart Routing product system",
    poster: "/media/kublaro/routing-v1.jpg",
    video: "/media/kublaro/routing-v1.mp4",
    format: "wide",
  },
] as const;

const developArgentinaMedia = [
  {
    label: "Interactive Argentina map on mobile",
    poster: "/media/developargentina/map-mobile-v2.jpg",
    video: "/media/developargentina/map-mobile-v2.mp4",
    format: "ios",
  },
  {
    label: "Navigate from the homepage into defined software solutions",
    poster: "/media/developargentina/solutions-v2.jpg",
    video: "/media/developargentina/solutions-v2.mp4",
    format: "wide",
  },
  {
    label: "Enter the software resources and knowledge layer",
    poster: "/media/developargentina/resources-v2.jpg",
    video: "/media/developargentina/resources-v2.mp4",
    format: "wide",
  },
] as const;

const restaurantArgentinaMedia = [
  {
    label: "Responsive restaurant launch experience",
    poster: "/media/restaurantargentina/mobile-system-v1.jpg?v=2",
    video: "/media/restaurantargentina/mobile-system-v1.mp4?v=2",
    format: "ios",
  },
  {
    label: "Navigate the consulting system",
    poster: "/media/restaurantargentina/kit-v1.jpg?v=2",
    video: "/media/restaurantargentina/kit-v1.mp4?v=2",
    format: "wide",
  },
  {
    label: "Search the Argentine supplier directory",
    poster: "/media/restaurantargentina/tools-v1.jpg?v=2",
    video: "/media/restaurantargentina/tools-v1.mp4?v=2",
    format: "wide",
  },
] as const;

const restaurantMarginMedia = [
  {
    label: "Mobile margin workspace and next-best action",
    poster: "/media/restaurantmargin/mobile-free-scan-v1.jpg?v=1",
    video: "/media/restaurantmargin/mobile-free-scan-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Live profit dashboard and menu-engineering insights",
    poster: "/media/restaurantmargin/profit-dashboard-v1.jpg?v=1",
    video: "/media/restaurantmargin/profit-dashboard-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Editorial product system and free workspace",
    poster: "/media/restaurantmargin/editorial-workspace-v1.jpg?v=1",
    video: "/media/restaurantmargin/editorial-workspace-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const eatEasierMedia = [
  {
    label: "Chef Bento inside the Eat Easier iOS app",
    poster: "/media/eateasier/chef-bento-v3.jpg",
    video: "/media/eateasier/chef-bento-v3.mp4",
    format: "ios",
  },
  {
    label: "Bento recipe catalog inside the native Eat Easier app",
    poster: "/media/eateasier/bento-recipes-v4.jpg?v=4",
    video: "/media/eateasier/bento-recipes-v4.mp4?v=4",
    format: "ios",
  },
  {
    label: "Physical Bento Box with a complete meal",
    poster: "/media/eateasier/bento-product-v3.jpg",
    video: "/media/eateasier/bento-product-v3.mp4",
    format: "wide",
  },
] as const;

const jornalDaIAMedia = [
  {
    label: "Jornal da IA responsive daily newsroom",
    poster: "/media/jornaldaia/mobile-newsroom-v1.jpg?v=1",
    video: "/media/jornaldaia/mobile-newsroom-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Daily news radar and editorial homepage",
    poster: "/media/jornaldaia/home-newsroom-v1.jpg?v=1",
    video: "/media/jornaldaia/home-newsroom-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Sourced article and analysis reading system",
    poster: "/media/jornaldaia/article-reading-v1.jpg?v=1",
    video: "/media/jornaldaia/article-reading-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const radarDeIAMedia = [
  {
    label: "Radar de IA responsive daily newsroom",
    poster: "/media/radardeia/mobile-newsroom-v1.jpg?v=2",
    video: "/media/radardeia/mobile-newsroom-v1.mp4?v=2",
    format: "ios",
  },
  {
    label: "Live news radar and Spanish editorial homepage",
    poster: "/media/radardeia/home-radar-v1.jpg?v=2",
    video: "/media/radardeia/home-radar-v1.mp4?v=2",
    format: "wide",
  },
  {
    label: "Radar de IA sourced article reading system",
    poster: "/media/radardeia/article-reading-v1.jpg?v=2",
    video: "/media/radardeia/article-reading-v1.mp4?v=2",
    format: "wide",
  },
] as const;

const nolleiMedia = [
  {
    label: "Nollei responsive made-to-order store",
    poster: "/media/nollei/mobile-store-v1.jpg?v=1",
    video: "/media/nollei/mobile-store-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Nollei editorial commerce homepage and collections",
    poster: "/media/nollei/editorial-home-v1.jpg?v=1",
    video: "/media/nollei/editorial-home-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Nollei searchable catalog and made-to-order commerce",
    poster: "/media/nollei/product-commerce-v1.jpg?v=1",
    video: "/media/nollei/product-commerce-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const codeAustralMedia = [
  {
    label: "Code Austral responsive studio journey",
    poster: "/media/codeaustral/mobile-studio-v1.jpg?v=1",
    video: "/media/codeaustral/mobile-studio-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Interactive project carousel with project-specific 3D forms",
    poster: "/media/codeaustral/projects-3d-carousel-v2.jpg?v=2",
    video: "/media/codeaustral/projects-3d-carousel-v2.mp4?v=2",
    format: "wide",
  },
  {
    label: "Austral postcard archive and responsive carousel",
    poster: "/media/codeaustral/austral-postcards-v2.jpg?v=2",
    video: "/media/codeaustral/austral-postcards-v2.mp4?v=2",
    format: "wide",
  },
] as const;

const linkMasMedia = [
  {
    label: "Link Más mobile claim and creator journey",
    poster: "/media/linkmas/mobile-product-v1.jpg?v=1",
    video: "/media/linkmas/mobile-product-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Interactive bio link, short URL, QR and analytics workbench",
    poster: "/media/linkmas/product-system-v1.jpg?v=1",
    video: "/media/linkmas/product-system-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Audience, utility library and transparent plan system",
    poster: "/media/linkmas/growth-system-v1.jpg?v=1",
    video: "/media/linkmas/growth-system-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const genetiqoMedia = [
  {
    label: "Native Today, Journal, Trends and Visit Brief flow",
    poster: "/media/genetiqo/ios-journal-v1.jpg?v=1",
    video: "/media/genetiqo/ios-journal-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Genetiqo Vault product and reporting system",
    poster: "/media/genetiqo/web-product-v1.jpg?v=1",
    video: "/media/genetiqo/web-product-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Privacy-first records, exports and Pro system",
    poster: "/media/genetiqo/privacy-reports-v1.jpg?v=1",
    video: "/media/genetiqo/privacy-reports-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const foodelopersMedia = [
  {
    eyebrow: "Live demo · restaurant SaaS",
    label: "Orderly",
    poster: "/media/foodelopers/orderly-demo-v1.jpg?v=1",
    video: "/media/foodelopers/orderly-demo-v1.mp4?v=1",
    format: "ios",
  },
  {
    eyebrow: "Live demo · multi-restaurant",
    label: "Forkit",
    poster: "/media/foodelopers/forkit-demo-v1.jpg?v=1",
    video: "/media/foodelopers/forkit-demo-v1.mp4?v=1",
    format: "wide",
  },
  {
    eyebrow: "Live demo · delivery and POS",
    label: "Plateform",
    poster: "/media/foodelopers/plateform-demo-v1.jpg?v=1",
    video: "/media/foodelopers/plateform-demo-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const bunBanditMedia = [
  {
    label: "Bun Bandit 2.0 native recipe run",
    poster: "/media/bunbandit/ios-gameplay-v2.jpg?v=2",
    video: "/media/bunbandit/ios-gameplay-v2.mp4?v=2",
    format: "ios",
  },
  {
    label: "Bun Bandit world and gameplay system",
    poster: "/media/bunbandit/world-system-v1.jpg?v=2",
    video: "/media/bunbandit/world-system-v1.mp4?v=2",
    format: "wide",
  },
  {
    label: "Bun Bandit release story and iPhone product",
    poster: "/media/bunbandit/web-story-v1.jpg?v=2",
    video: "/media/bunbandit/web-story-v1.mp4?v=2",
    format: "wide",
  },
] as const;

const rockClashMedia = [
  {
    label: "Build a playable song in the native Creator",
    poster: "/media/rockclash/creator-loop.jpg?v=2",
    video: "/media/rockclash/creator-loop.mp4?v=2",
    format: "ios",
  },
  {
    label: "Play the live four-lane rhythm chart",
    poster: "/media/rockclash/gameplay-loop.jpg?v=2",
    video: "/media/rockclash/gameplay-loop.mp4?v=2",
    format: "ios",
  },
  {
    label: "Move between unlockable live stages",
    poster: "/media/rockclash/stages-loop.jpg?v=2",
    video: "/media/rockclash/stages-loop.mp4?v=2",
    format: "ios",
  },
] as const;

const makoBitsMedia = [
  {
    label: "Build routes on the live merge board",
    poster: "/media/makobits/merge-loop.jpg?v=1",
    video: "/media/makobits/merge-loop.mp4?v=1",
    format: "ios",
  },
  {
    label: "Care for a Mako in its pixel room",
    poster: "/media/makobits/room-loop.jpg?v=1",
    video: "/media/makobits/room-loop.mp4?v=1",
    format: "ios",
  },
  {
    label: "Explore the collectible Mako Codex",
    poster: "/media/makobits/collection-loop.jpg?v=1",
    video: "/media/makobits/collection-loop.mp4?v=1",
    format: "ios",
  },
] as const;

const talkMaestroMedia = [
  {
    label: "Choose a room and build the next speaking rep",
    poster: "/media/talkmaestro/practice-loop.jpg?v=2",
    video: "/media/talkmaestro/practice-loop.mp4?v=2",
    format: "ios",
  },
  {
    label: "Prepare a high-pressure answer with Live Roleplay",
    poster: "/media/talkmaestro/live-loop.jpg?v=2",
    video: "/media/talkmaestro/live-loop.mp4?v=2",
    format: "ios",
  },
  {
    label: "Turn every take into a precise coach readout",
    poster: "/media/talkmaestro/readout-loop.jpg?v=2",
    video: "/media/talkmaestro/readout-loop.mp4?v=2",
    format: "ios",
  },
] as const;

const jogaIAMedia = [
  {
    eyebrow: "01 · Voxel adventure",
    label: "Mundo Móvel",
    poster: "/media/jogaia/mundo-loop.jpg?v=2",
    video: "/media/jogaia/mundo-loop.mp4?v=2",
    format: "web",
  },
  {
    eyebrow: "02 · Space merge",
    label: "Cosmos Merge",
    poster: "/media/jogaia/cosmos-loop.jpg?v=2",
    video: "/media/jogaia/cosmos-loop.mp4?v=2",
    format: "web",
  },
  {
    eyebrow: "03 · Impossible racing",
    label: "Rift Rider",
    poster: "/media/jogaia/rift-loop.jpg?v=2",
    video: "/media/jogaia/rift-loop.mp4?v=2",
    format: "web",
  },
] as const;

const iaEspacioMedia = [
  {
    label: "Responsive room redesign journey",
    poster: "/media/iaespacio/mobile-product-v1.jpg?v=1",
    video: "/media/iaespacio/mobile-product-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Interactive before-and-after and room styles",
    poster: "/media/iaespacio/transformation-system-v1.jpg?v=1",
    video: "/media/iaespacio/transformation-system-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Live IA Espacio creation Studio",
    poster: "/media/iaespacio/studio-system-v1.jpg?v=1",
    video: "/media/iaespacio/studio-system-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const iglesiaJesusMedia = [
  {
    label: "Responsive prayer and scripture journey",
    poster: "/media/iglesiajesus/mobile-faith-journey-v1.jpg?v=3",
    video: "/media/iglesiajesus/mobile-faith-journey-v1.mp4?v=3",
    format: "ios",
  },
  {
    label: "Guided prayer and complete Bible system",
    poster: "/media/iglesiajesus/prayer-bible-system-v1.jpg?v=3",
    video: "/media/iglesiajesus/prayer-bible-system-v1.mp4?v=3",
    format: "wide",
  },
  {
    label: "Plans, devotionals and spiritual library",
    poster: "/media/iglesiajesus/daily-rhythm-system-v1.jpg?v=3",
    video: "/media/iglesiajesus/daily-rhythm-system-v1.mp4?v=3",
    format: "wide",
  },
] as const;

const logoAhoraMedia = [
  {
    label: "LogoAhora mobile identity journey",
    poster: "/media/logoahora/mobile-identity-v1.jpg?v=2",
    video: "/media/logoahora/mobile-identity-v1.mp4?v=2",
    format: "ios",
  },
  {
    label: "Live brief and identity direction studio",
    poster: "/media/logoahora/identity-studio-v1.jpg?v=2",
    video: "/media/logoahora/identity-studio-v1.mp4?v=2",
    format: "wide",
  },
  {
    label: "Applied brand system, styles and pricing",
    poster: "/media/logoahora/brand-system-v1.jpg?v=2",
    video: "/media/logoahora/brand-system-v1.mp4?v=2",
    format: "wide",
  },
] as const;

const matrixAgenciaMedia = [
  {
    label: "Matrix WebGL entrance and mobile navigation",
    poster: "/media/matrixagencia/mobile-matrix-v1.jpg?v=1",
    video: "/media/matrixagencia/mobile-matrix-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Web 3D and gamified experience library",
    poster: "/media/matrixagencia/experience-library-v1.jpg?v=1",
    video: "/media/matrixagencia/experience-library-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Commercial automation blueprint system",
    poster: "/media/matrixagencia/automation-system-v1.jpg?v=1",
    video: "/media/matrixagencia/automation-system-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const youAreEarlyMedia = [
  {
    label: "You Are Early mobile research experience",
    poster: "/media/youareearly/mobile-book-v1.jpg?v=1",
    video: "/media/youareearly/mobile-book-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "The evidence-backed case for being early",
    poster: "/media/youareearly/early-case-v1.jpg?v=1",
    video: "/media/youareearly/early-case-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Research architecture, action system and commerce flow",
    poster: "/media/youareearly/book-system-v1.jpg?v=1",
    video: "/media/youareearly/book-system-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const menuCraftersMedia = [
  {
    label: "MenuCrafters mobile brief and Studio journey",
    poster: "/media/menucrafters/mobile-studio-v1.jpg?v=1",
    video: "/media/menucrafters/mobile-studio-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Live AI menu Studio and publishing system",
    poster: "/media/menucrafters/studio-system-v1.jpg?v=1",
    video: "/media/menucrafters/studio-system-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Restaurant template library and menu directions",
    poster: "/media/menucrafters/template-library-v1.jpg?v=1",
    video: "/media/menucrafters/template-library-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const xagodMedia = [
  {
    label: "XAGOD mobile Witness and touch-control journey",
    poster: "/media/xagod/mobile-witness-v1.jpg?v=1",
    video: "/media/xagod/mobile-witness-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "The First Render world, map and quest system",
    poster: "/media/xagod/world-quest-v1.jpg?v=1",
    video: "/media/xagod/world-quest-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Nine-class targeting and combat system",
    poster: "/media/xagod/combat-system-v1.jpg?v=1",
    video: "/media/xagod/combat-system-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const postRaptorMedia = [
  {
    label: "PostRaptor interactive brand replay on mobile",
    poster: "/media/postraptor/mobile-demo-v1.jpg?v=1",
    video: "/media/postraptor/mobile-demo-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Website analysis and brand-design pipeline",
    poster: "/media/postraptor/brand-pipeline-v1.jpg?v=1",
    video: "/media/postraptor/brand-pipeline-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Three original social routes from one website",
    poster: "/media/postraptor/social-outputs-v1.jpg?v=1",
    video: "/media/postraptor/social-outputs-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const pitchDeckGenieMedia = [
  {
    label: "Responsive founder workflow and Story Health system",
    poster: "/media/pitchdeckgenie/mobile-founder-workflow-v1.jpg?v=2",
    video: "/media/pitchdeckgenie/mobile-founder-workflow-v1.mp4?v=2",
    format: "ios",
  },
  {
    label: "Investor argument workspace, evidence gaps and deck editor",
    poster: "/media/pitchdeckgenie/story-health-system-v1.jpg?v=2",
    video: "/media/pitchdeckgenie/story-health-system-v1.mp4?v=2",
    format: "wide",
  },
  {
    label: "Generated thirteen-slide investor deck with live navigation",
    poster: "/media/pitchdeckgenie/generated-deck-v1.jpg?v=2",
    video: "/media/pitchdeckgenie/generated-deck-v1.mp4?v=2",
    format: "wide",
  },
] as const;

const vendaKitMedia = [
  {
    label: "Responsive product transformation and complete sales kit",
    poster: "/media/vendakit/mobile-transformation-v1.jpg?v=1",
    video: "/media/vendakit/mobile-transformation-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Real upload, structured briefing and Pix credit workflow",
    poster: "/media/vendakit/creation-workflow-v1.jpg?v=1",
    video: "/media/vendakit/creation-workflow-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Marketplace-ready before and after output gallery",
    poster: "/media/vendakit/marketplace-gallery-v1.jpg?v=1",
    video: "/media/vendakit/marketplace-gallery-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const avatarmaticMedia = [
  {
    label: "Responsive Avatarmatic product and live avatar demo",
    poster: "/media/avatarmatic/mobile-live-avatar-v1.jpg?v=1",
    video: "/media/avatarmatic/mobile-live-avatar-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Interactive support, sales and education avatar scenarios",
    poster: "/media/avatarmatic/interactive-scenarios-v1.jpg?v=1",
    video: "/media/avatarmatic/interactive-scenarios-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Authenticated AvatarMatic Studio dashboard and avatar builder",
    poster: "/media/avatarmatic/studio-workspace-v2.jpg?v=2",
    video: "/media/avatarmatic/studio-workspace-v2.mp4?v=2",
    format: "wide",
  },
] as const;

const callTurboMedia = [
  {
    label: "Responsive CallTurbo proposition and call-operations platform",
    poster: "/media/callturbo/mobile-call-operations-v1.jpg?v=1",
    video: "/media/callturbo/mobile-call-operations-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Authenticated operations dashboard across calls, campaigns and support",
    poster: "/media/callturbo/admin-operations-v2.jpg?v=2",
    video: "/media/callturbo/admin-operations-v2.mp4?v=2",
    format: "wide",
  },
  {
    label: "Voice-agent workspace and live agent configuration flow",
    poster: "/media/callturbo/voice-agent-builder-v2.jpg?v=2",
    video: "/media/callturbo/voice-agent-builder-v2.mp4?v=2",
    format: "wide",
  },
] as const;

const propBotStudioMedia = [
  {
    label: "AI chatbot qualifying a property lead on mobile",
    eyebrow: "01 · Chat",
    poster: "/media/propbotstudio/chatbot-mobile-v1.jpg?v=1",
    video: "/media/propbotstudio/chatbot-mobile-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "CRM dashboard connecting lead score, source and activity",
    eyebrow: "02 · Operate",
    poster: "/media/propbotstudio/crm-dashboard-v1.jpg?v=1",
    video: "/media/propbotstudio/crm-dashboard-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Voice agent qualifying and scheduling a real-estate enquiry",
    eyebrow: "03 · Answer",
    poster: "/media/propbotstudio/voice-agent-v1.jpg?v=1",
    video: "/media/propbotstudio/voice-agent-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const manualDelRestauranteMedia = [
  {
    label: "Mobile symptom selector routing an operator to the right manual",
    eyebrow: "01 · Diagnose",
    poster: "/media/manualdelrestaurante/mobile-selector-v1.jpg?v=1",
    video: "/media/manualdelrestaurante/mobile-selector-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Searchable 23-manual library opening into a complete guide",
    eyebrow: "02 · Apply",
    poster: "/media/manualdelrestaurante/library-guide-v1.jpg?v=1",
    video: "/media/manualdelrestaurante/library-guide-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Focused and full-restaurant asynchronous consulting system",
    eyebrow: "03 · Adapt",
    poster: "/media/manualdelrestaurante/consulting-system-v1.jpg?v=1",
    video: "/media/manualdelrestaurante/consulting-system-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const orcaNaHoraMedia = [
  {
    label: "Mobile proposal setup from optional AI brief to a ready service template",
    eyebrow: "01 · Start",
    poster: "/media/orcanahora/mobile-builder-v1.jpg?v=2",
    video: "/media/orcanahora/mobile-builder-v1.mp4?v=2",
    format: "ios",
  },
  {
    label: "Complete proposal Studio with editable scope and live client preview",
    eyebrow: "02 · Build",
    poster: "/media/orcanahora/proposal-studio-v1.jpg?v=2",
    video: "/media/orcanahora/proposal-studio-v1.mp4?v=2",
    format: "wide",
  },
  {
    label: "Reusable proposal models and optional pay-as-needed AI system",
    eyebrow: "03 · Accelerate",
    poster: "/media/orcanahora/templates-ai-v1.jpg?v=2",
    video: "/media/orcanahora/templates-ai-v1.mp4?v=2",
    format: "wide",
  },
] as const;

const takeAICourseMedia = [
  {
    label: "Mobile learning path from school benefits into the flagship 30-day program",
    eyebrow: "01 · Start",
    poster: "/media/takeaicourse/mobile-learning-v1.jpg?v=1",
    video: "/media/takeaicourse/mobile-learning-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Complete 30-day course plan with modules, lessons, projects and certificate",
    eyebrow: "02 · Learn",
    poster: "/media/takeaicourse/course-plan-v1.jpg?v=1",
    video: "/media/takeaicourse/course-plan-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Connected dashboard for courses, prompts, Agents, Skills and reusable resources",
    eyebrow: "03 · Build",
    poster: "/media/takeaicourse/learning-system-v1.jpg?v=1",
    video: "/media/takeaicourse/learning-system-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const uglyProfitableMedia = [
  {
    label: "Mobile discovery across the complete business catalog and decision filters",
    eyebrow: "01 · Discover",
    poster: "/media/uglyprofitable/mobile-discovery-v1.jpg?v=1",
    video: "/media/uglyprofitable/mobile-discovery-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Business intelligence profile with economics, launch playbook and customer path",
    eyebrow: "02 · Research",
    poster: "/media/uglyprofitable/business-intelligence-v1.jpg?v=1",
    video: "/media/uglyprofitable/business-intelligence-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "State-aware recommendations and head-to-head business comparison system",
    eyebrow: "03 · Decide",
    poster: "/media/uglyprofitable/decision-system-v1.jpg?v=1",
    video: "/media/uglyprofitable/decision-system-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const zeroToPublicMedia = [
  {
    label: "Responsive journey through the operating archive and its core product narrative",
    eyebrow: "01 · Explore",
    poster: "/media/zerotopublic/mobile-book-v1.jpg?v=1",
    video: "/media/zerotopublic/mobile-book-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Structured evidence system connecting fifty public internet products",
    eyebrow: "02 · Study",
    poster: "/media/zerotopublic/book-evidence-v1.jpg?v=1",
    video: "/media/zerotopublic/book-evidence-v1.mp4?v=1",
    format: "wide",
  },
  {
    label: "Route-based decision workflow with representative validation and MVP outputs",
    eyebrow: "03 · Decide",
    poster: "/media/zerotopublic/decision-reports-v1.jpg?v=1",
    video: "/media/zerotopublic/decision-reports-v1.mp4?v=1",
    format: "wide",
  },
] as const;

const qrLojaMedia = [
  {
    label: "Live mobile storefront from product discovery into a complete checkout state",
    poster: "/media/qrloja/mobile-storefront-v1.jpg?v=1",
    video: "/media/qrloja/mobile-storefront-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Native service scheduling with real availability and customer details",
    poster: "/media/qrloja/mobile-booking-v1.jpg?v=1",
    video: "/media/qrloja/mobile-booking-v1.mp4?v=1",
    format: "ios",
  },
  {
    label: "Eight-step store builder with autosave and a live storefront preview",
    poster: "/media/qrloja/store-builder-v1.jpg?v=1",
    video: "/media/qrloja/store-builder-v1.mp4?v=1",
    format: "wide",
  },
] as const;

type ProductMedia = {
  label: string;
  eyebrow?: string;
  poster: string;
  video?: string;
  image?: string;
  format: "ios" | "web" | "wide" | "detail";
};

const mediaPhaseOffsets = [0.12, 0.48, 0.82] as const;

function VisibleLoopVideo({
  src,
  poster,
  label,
  startFraction = 0,
}: {
  src: string;
  poster: string;
  label: string;
  startFraction?: number;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let shouldPlay = false;
    let phaseApplied = false;
    let fallbackTimer = 0;

    const playNow = () => {
      if (!shouldPlay) return;
      video.playbackRate = 1;
      void video.play().catch(() => undefined);
    };

    const applyStartPhase = () => {
      if (!shouldPlay || phaseApplied || !Number.isFinite(video.duration) || video.duration <= 0.25) return;
      const safeFraction = Math.min(0.92, Math.max(0, startFraction));
      const targetTime = Math.min(video.duration - 0.08, video.duration * safeFraction);
      phaseApplied = true;
      video.pause();
      try {
        video.currentTime = Math.max(0, targetTime);
      } catch {
        phaseApplied = false;
        return;
      }
      window.clearTimeout(fallbackTimer);
      fallbackTimer = window.setTimeout(playNow, 120);
    };

    const onMetadataReady = () => {
      applyStartPhase();
      if (phaseApplied && !video.seeking) playNow();
    };

    const onSeeked = () => {
      window.clearTimeout(fallbackTimer);
      playNow();
    };

    video.addEventListener("loadedmetadata", onMetadataReady);
    video.addEventListener("durationchange", onMetadataReady);
    video.addEventListener("canplay", onMetadataReady);
    video.addEventListener("seeked", onSeeked);

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
        shouldPlay = true;
        if (video.preload !== "auto") {
          video.preload = "auto";
          video.load();
        }
        if (video.readyState >= HTMLMediaElement.HAVE_METADATA) onMetadataReady();
        if (phaseApplied && !video.seeking) playNow();
      } else {
        shouldPlay = false;
        video.pause();
      }
    }, { threshold: [0, 0.2, 0.6] });

    observer.observe(video);
    return () => {
      shouldPlay = false;
      observer.disconnect();
      window.clearTimeout(fallbackTimer);
      video.removeEventListener("loadedmetadata", onMetadataReady);
      video.removeEventListener("durationchange", onMetadataReady);
      video.removeEventListener("canplay", onMetadataReady);
      video.removeEventListener("seeked", onSeeked);
      video.pause();
    };
  }, [startFraction]);

  return (
    <video
      ref={videoRef}
      muted
      loop
      playsInline
      preload="none"
      poster={mediaAssetUrl(poster)}
      aria-label={label}
      data-start-fraction={startFraction}
    >
      <source src={mediaAssetUrl(src)} type="video/mp4" />
    </video>
  );
}

function InteractiveMediaDeck({
  items,
  label,
  hint,
  showLabels = false,
  staggered = true,
}: {
  items: readonly ProductMedia[];
  label: string;
  hint: string;
  showLabels?: boolean;
  staggered?: boolean;
}) {
  const allLandscape = items.every((item) => item.format !== "ios");
  const hasPortrait = items.some((item) => item.format === "ios");
  const allPortrait = items.every((item) => item.format === "ios");

  return (
    <div className={`media-deck real-media-deck${allLandscape ? " is-all-landscape" : ""}${hasPortrait ? " has-portrait" : ""}${allPortrait ? " is-all-portrait" : ""}${showLabels ? " is-highlighted-demos" : ""}`} aria-label={label}>
      {items.map((media, cardIndex) => (
        <figure
          className={`real-media-card real-media-card-${cardIndex + 1} is-${media.format}`}
          key={media.label}
          tabIndex={0}
          aria-label={media.label}
        >
          {media.image ? (
            <img src={mediaAssetUrl(media.image)} alt={media.label} />
          ) : (
            <VisibleLoopVideo
              src={media.video ?? ""}
              poster={media.poster}
              label={media.label}
              startFraction={staggered ? mediaPhaseOffsets[cardIndex % mediaPhaseOffsets.length] : 0}
            />
          )}
        </figure>
      ))}
      <div className="deck-hint"><span /> {hint}</div>
    </div>
  );
}

function ArtMuseumMediaDeck() {
  return (
    <InteractiveMediaDeck
      items={artMuseumMedia}
      label="Real ArtMuseum Brasil product media"
      hint="Hover to move through the museum"
    />
  );
}

function AgroFounderMediaDeck() {
  return <InteractiveMediaDeck items={agroFounderMedia} label="Real AgroFounder product demo" hint="" staggered={false} />;
}

function AstraCAMMediaDeck() {
  return <InteractiveMediaDeck items={astraCAMMedia} label="Real AstraCAM native iOS product media" hint="" staggered={false} />;
}

function AustralLeatherMediaDeck() {
  return <InteractiveMediaDeck items={australLeatherMedia} label="Real Austral Leather website and product library" hint="" staggered={false} />;
}

function EaxyMediaDeck() {
  return <InteractiveMediaDeck items={eaxyMedia} label="Real Eaxy AI creation-studio media" hint="" staggered={false} />;
}

function HuntorizonMediaDeck() {
  return <InteractiveMediaDeck items={huntorizonMedia} label="Real Huntorizon native gameplay" hint="" staggered={false} />;
}

function MiniBichoMediaDeck() {
  return <InteractiveMediaDeck items={miniBichoMedia} label="Real MiniBicho native gameplay" hint="" staggered={false} />;
}

function SkullSailorMediaDeck() {
  return <InteractiveMediaDeck items={skullSailorMedia} label="Real Skull Sailor product media" hint="" staggered={false} />;
}

function AntesDepoisMediaDeck() {
  return <InteractiveMediaDeck items={antesDepoisMedia} label="Real AntesDepois comparison, proof and preset media" hint="" />;
}

function AIClasesMediaDeck() {
  return (
    <InteractiveMediaDeck
      items={aiClasesMedia}
      label="Real AIClases product media"
      hint="Hover to explore the learning platform"
    />
  );
}

function AulasDeIAMediaDeck() {
  return (
    <InteractiveMediaDeck
      items={aulasDeIAMedia}
      label="Real AulasDeIA dark product media"
      hint=""
    />
  );
}

function RoomRenovationMediaDeck() {
  return (
    <InteractiveMediaDeck
      items={roomRenovationMedia}
      label="Real RoomRenovation native and web product media"
      hint=""
    />
  );
}

function FoodPhotoMediaDeck() {
  return (
    <InteractiveMediaDeck
      items={foodPhotoMedia}
      label="Real FoodPhoto 2.0 native and web product media"
      hint=""
    />
  );
}

function FotoProfissaMediaDeck() {
  return <InteractiveMediaDeck items={fotoProfissaMedia} label="Real FotoProfissa mobile, transformation and package media" hint="" />;
}

function CursaloMediaDeck() {
  return <InteractiveMediaDeck items={cursaloMedia} label="Real Cursalo catalog, learning route and course media" hint="" />;
}

function KublaroMediaDeck() {
  return <InteractiveMediaDeck items={kublaroMedia} label="Real Kublaro product media" hint="" />;
}

function DevelopArgentinaMediaDeck() {
  return <InteractiveMediaDeck items={developArgentinaMedia} label="Real Develop Argentina product media" hint="" />;
}

function RestaurantArgentinaMediaDeck() {
  return <InteractiveMediaDeck items={restaurantArgentinaMedia} label="Real Restaurant Argentina product media" hint="" />;
}

function RestaurantMarginMediaDeck() {
  return <InteractiveMediaDeck items={restaurantMarginMedia} label="Real RestaurantMargin workspace, insights and product media" hint="" />;
}

function EatEasierMediaDeck() {
  return <InteractiveMediaDeck items={eatEasierMedia} label="Real Eat Easier native, web and Bento Box media" hint="" />;
}

function JornalDaIAMediaDeck() {
  return <InteractiveMediaDeck items={jornalDaIAMedia} label="Real Jornal da IA newsroom media" hint="" />;
}

function RadarDeIAMediaDeck() {
  return <InteractiveMediaDeck items={radarDeIAMedia} label="Real Radar de IA newsroom media" hint="" />;
}

function NolleiMediaDeck() {
  return <InteractiveMediaDeck items={nolleiMedia} label="Real Nollei editorial commerce media" hint="" />;
}

function CodeAustralMediaDeck() {
  return <InteractiveMediaDeck items={codeAustralMedia} label="Real Code Austral studio, services and portfolio media" hint="" />;
}

function LinkMasMediaDeck() {
  return <InteractiveMediaDeck items={linkMasMedia} label="Real Link Más mobile, product and growth media" hint="" />;
}

function GenetiqoMediaDeck() {
  return <InteractiveMediaDeck items={genetiqoMedia} label="Real Genetiqo Vault native, web and privacy media" hint="" />;
}

function FoodelopersMediaDeck() {
  return <InteractiveMediaDeck items={foodelopersMedia} label="Three live Foodelopers product demos" hint="" showLabels />;
}

function BunBanditMediaDeck() {
  return <InteractiveMediaDeck items={bunBanditMedia} label="Real Bun Bandit 2.0 native gameplay and product media" hint="" />;
}

function RockClashMediaDeck() {
  return <InteractiveMediaDeck items={rockClashMedia} label="Latest RockClash native game screens" hint="" />;
}

function MakoBitsMediaDeck() {
  return <InteractiveMediaDeck items={makoBitsMedia} label="Real MakoBits Merge gameplay, room and Codex screens" hint="" />;
}

function JogaIAMediaDeck() {
  return <InteractiveMediaDeck items={jogaIAMedia} label="Real gameplay from the JogaIA browser arcade" hint="" showLabels />;
}

function TalkMaestroMediaDeck() {
  return <InteractiveMediaDeck items={talkMaestroMedia} label="Latest TalkMaestro coach plan, Live Roleplay and readout screens" hint="" />;
}

function IAEspacioMediaDeck() {
  return <InteractiveMediaDeck items={iaEspacioMedia} label="Real IA Espacio room transformations and creation Studio" hint="" />;
}

function IglesiaJesusMediaDeck() {
  return <InteractiveMediaDeck items={iglesiaJesusMedia} label="Real Iglesia de Jesús prayer, Bible and daily practice media" hint="" />;
}

function LogoAhoraMediaDeck() {
  return <InteractiveMediaDeck items={logoAhoraMedia} label="Real LogoAhora identity studio, logo library and brand system" hint="" />;
}

function MatrixAgenciaMediaDeck() {
  return <InteractiveMediaDeck items={matrixAgenciaMedia} label="Real Matrix WebGL, interactive campaign and automation media" hint="" />;
}

function YouAreEarlyMediaDeck() {
  return <InteractiveMediaDeck items={youAreEarlyMedia} label="Real You Are Early book, evidence and publishing media" hint="" />;
}

function MenuCraftersMediaDeck() {
  return <InteractiveMediaDeck items={menuCraftersMedia} label="Real MenuCrafters Studio, mobile and template media" hint="" />;
}

function XagodMediaDeck() {
  return <InteractiveMediaDeck items={xagodMedia} label="Real XAGOD mobile, world and combat media" hint="" />;
}

function PostRaptorMediaDeck() {
  return <InteractiveMediaDeck items={postRaptorMedia} label="Real PostRaptor demo, brand extraction and social output media" hint="" />;
}

function PitchDeckGenieMediaDeck() {
  return <InteractiveMediaDeck items={pitchDeckGenieMedia} label="Real PitchDeckGenie founder workflow, Story Health and generated deck media" hint="" />;
}

function VendaKitMediaDeck() {
  return <InteractiveMediaDeck items={vendaKitMedia} label="Real VendaKit transformation, creation workflow and marketplace output media" hint="" />;
}

function AvatarmaticMediaDeck() {
  return <InteractiveMediaDeck items={avatarmaticMedia} label="Real Avatarmatic mobile, live scenario and template workflow media" hint="" />;
}

function CallTurboMediaDeck() {
  return <InteractiveMediaDeck items={callTurboMedia} label="Real CallTurbo mobile, call-flow and operational outcome media" hint="" />;
}

function PropBotStudioMediaDeck() {
  return <InteractiveMediaDeck items={propBotStudioMedia} label="Three working PropBot Studio real-estate automation demos" hint="" showLabels />;
}

function ManualDelRestauranteMediaDeck() {
  return <InteractiveMediaDeck items={manualDelRestauranteMedia} label="Real Manual del Restaurante selector, library and consulting media" hint="" showLabels />;
}

function OrcaNaHoraMediaDeck() {
  return <InteractiveMediaDeck items={orcaNaHoraMedia} label="Real OrçaNaHora mobile, proposal Studio and optional AI media" hint="" showLabels />;
}

function UglyProfitableMediaDeck() {
  return <InteractiveMediaDeck items={uglyProfitableMedia} label="Real Ugly Profitable discovery, research and decision media" hint="" showLabels />;
}

function TakeAICourseMediaDeck() {
  return <InteractiveMediaDeck items={takeAICourseMedia} label="Real TakeAICourse mobile, curriculum and learning system media" hint="" showLabels />;
}

function ZeroToPublicMediaDeck() {
  return <InteractiveMediaDeck items={zeroToPublicMedia} label="Real Zero to Public archive, evidence and decision media" hint="" showLabels />;
}

function QRLojaMediaDeck() {
  return <InteractiveMediaDeck items={qrLojaMedia} label="Real QRLoja storefront, scheduling and store-builder media" hint="" />;
}

function StackGrid({ stack }: { stack: string[] }) {
  return (
    <div className="stack-grid" aria-label="Technology stack">
      {stack.map((technology) => {
        const Icon = stackIcons[technology] ?? Code2;
        return (
          <div className="stack-item" key={technology}>
            <span className="stack-icon"><Icon size={19} strokeWidth={1.7} aria-hidden /></span>
            <span>{technology}</span>
          </div>
        );
      })}
    </div>
  );
}

function ArtMuseumStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the project</span>
      <p>
        ArtMuseum Brasil makes a Brazilian art collection explorable as a place, not a list.
        Visitors walk through themed rooms, approach works in context, open focused details,
        follow curated routes and save favorites.
      </p>
      <span>How it is built</span>
      <p>
        Next.js and React host the product layer. React Three Fiber, Drei and Three.js render
        the museum world, while Zustand coordinates room navigation, camera controls, artwork
        focus and local interaction state across web and iOS.
      </p>
    </div>
  );
}

function AIClasesStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the project</span>
      <p>
        AIClases is a Spanish-language applied AI school for Latin America and Spain.
        Its product combines 25 practical courses, structured learning routes, certificates,
        books, prompts, Agents and reusable Skills in one evolving catalog.
      </p>
      <span>Business and platform</span>
      <p>
        Students can buy individual courses or access the full catalog through an annual
        membership. Next.js powers the storefront and learning experience, with PostgreSQL
        for product data and Stripe for enrollment and payments.
      </p>
    </div>
  );
}

function AulasDeIAStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the project</span>
      <p>
        Aulas de IA is a Portuguese-language applied AI school for Brazilian professionals,
        creators and businesses. It brings courses, practical routes, books, prompts, Agents,
        Skills, Plugins and interactive demonstrations into one dark learning environment.
      </p>
      <span>Business and platform</span>
      <p>
        The product combines protected lessons, progress tracking, practical materials and
        verifiable certificates with paid access plans. Next.js powers the school and catalog,
        PostgreSQL manages learning data and Stripe supports enrollment and billing.
      </p>
    </div>
  );
}

function AntesDepoisStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the product</span>
      <p>
        AntesDepois keeps the original photo visible while a person chooses from 100 ready
        visual directions, adds an optional detail and judges the change with an interactive divider.
      </p>
      <span>From proof to production</span>
      <p>
        The first transformation is free without a card. Private files, high-quality downloads,
        automatic credit return on technical failure and one-off or monthly credits make the
        comparison usable for real business work.
      </p>
    </div>
  );
}

function RoomRenovationStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the project</span>
      <p>
        RoomRenovation AI helps homeowners, designers and property teams preview a change
        before committing to it. A real room photo becomes the starting point for exploring
        styles, materials and renovation intensity while keeping the original space recognizable.
      </p>
      <span>Product and platform</span>
      <p>
        The native SwiftUI app turns the workflow into a focused Studio with style controls,
        project history and plan management. The Next.js web product extends it with room-aware
        AI rendering, before-and-after exploration and Stripe-powered plans for higher-detail work.
      </p>
    </div>
  );
}

function FoodPhotoStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the project</span>
      <p>
        FoodPhoto AI is a photography studio built for restaurants, cafés, delivery brands and
        food creators. It improves lighting, background, sharpness and composition while keeping
        the real dish, ingredients, portion and plating recognizable.
      </p>
      <span>Product and platform</span>
      <p>
        FoodPhoto 2.0 brings the workflow to a native iOS Studio with camera-aware styles,
        layers and a mobile gallery. The web app adds AI photo editing, marketing assets,
        project organization and a large preset library, with Next.js, AI generation and
        Stripe-powered access working as one product.
      </p>
    </div>
  );
}

function FotoProfissaStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the product</span>
      <p>
        FotoProfissa turns one clear photo into natural portraits for documents, LinkedIn,
        résumés, weddings, birthdays, graduations and family moments while keeping the person recognizable.
      </p>
      <span>From photo to usable package</span>
      <p>
        The Studio combines occasion-aware direction with Brazilian 3×4 and 5×7 formats,
        social crops, private history and one-time PIX packages processed through Stripe.
      </p>
    </div>
  );
}

function CursaloStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the platform</span>
      <p>
        Cursalo organizes 141 Spanish-language courses around practical directions: AI,
        technology, data, business, design, communication, finance and career development.
      </p>
      <span>From discovery to completion</span>
      <p>
        Learners can begin with a goal, filter the full catalog, open a detailed curriculum,
        follow instructors and prove progress through a structured online course experience.
      </p>
    </div>
  );
}

function EaxyStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>One creation workspace</span>
      <p>
        Eaxy brings image, video, voice, writing and agent workflows into one clear product,
        reducing the friction of choosing tools and moving work between disconnected generators.
      </p>
      <span>Product and delivery</span>
      <p>
        Next.js and TypeScript structure the creation surfaces, OpenAI powers intelligent
        workflows, and Cloudflare keeps the public experience fast and globally available.
      </p>
    </div>
  );
}

function HuntorizonStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>Move through the hunt</span>
      <p>
        Huntorizon is built around forward motion: the player advances along a hostile road,
        reads approaching creatures and chooses when to fire, dodge or preserve vigor.
      </p>
      <span>Native game system</span>
      <p>
        SwiftUI frames the app while SpriteKit and Metal handle the live road, characters,
        encounter state and touch-first combat shown in the Simulator capture.
      </p>
    </div>
  );
}

function MiniBichoStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>A living pet universe</span>
      <p>
        MiniBicho connects collectible companions, rooms, care actions and small arcade worlds
        so every short session feeds a broader playful relationship with the pet.
      </p>
      <span>Native mobile product</span>
      <p>
        React Native and Expo coordinate gestures, haptics and app state while Three.js supports
        the visual world and StoreKit connects progression to the iOS product model.
      </p>
    </div>
  );
}

function SkullSailorStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>Piracy around Florianopolis</span>
      <p>
        Skull Sailor turns the coast into a stylized 3D pirate playground of full-sail movement,
        cannon fire, forts and horizontal touch-first exploration.
      </p>
      <span>Native game architecture</span>
      <p>
        SwiftUI organizes the interface and touch controls while a Metal renderer drives the sea,
        ships, islands and reusable simulation state inside the native iOS app.
      </p>
    </div>
  );
}

function KublaroStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the project</span>
      <p>
        Kublaro is a multilingual image and video studio for creators, marketers and small
        teams. A single brief can become product photography, campaign art, posters,
        characters, thumbnails or cinematic motion without managing a stack of model subscriptions.
      </p>
      <span>Product and routing</span>
      <p>
        Smart Routing reads the task, style, motion, resolution and speed required, then sends
        the prompt to the best-fit image or video model. Packs, specialized creation flows,
        commercial exports and credit plans turn the routing layer into a complete production tool.
      </p>
    </div>
  );
}

function DevelopArgentinaStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the project</span>
      <p>
        Develop Argentina is a software company built around the technology needs of real
        Argentine businesses: websites, ecommerce, mobile apps, SaaS products, AI agents
        and operational automation delivered as clearly defined services.
      </p>
      <span>Product and territory</span>
      <p>
        The platform combines a commercial service catalog, an educational resource layer
        and an Argentina Austral visual archive. The result connects software delivery with
        local industry, geography and a practical national point of view.
      </p>
    </div>
  );
}

function RestaurantArgentinaStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the project</span>
      <p>
        Restaurant Argentina is a complete operating system for opening, licensing and
        running a restaurant, bar, café or dark kitchen without improvising. It translates
        the Argentine market into practical guides, current costs and ready-to-use tools.
      </p>
      <span>Product and implementation</span>
      <p>
        WordPress structures the operating content and supplier layer, JavaScript powers the
        financial calculators, and WooCommerce with Mercado Pago connects access and fulfillment
        to one locally relevant commerce flow.
      </p>
    </div>
  );
}

function RestaurantMarginStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the product</span>
      <p>
        RestaurantMargin turns dish-level cost, price and weekly volume into live contribution,
        food-cost and menu-engineering decisions. Operators can edit the menu, switch currencies
        and see the impact without waiting for a server round trip.
      </p>
      <span>Product and system</span>
      <p>
        Next.js and TypeScript power the workspace. Deterministic calculations and local persistence
        keep free menus responsive and private, while DeepSeek assessments, saved Pro scenarios,
        exports and Stripe access layer AI and paid operating workflows on top.
      </p>
    </div>
  );
}

function EatEasierStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the product</span>
      <p>
        Eat Easier connects a real single-serving Bento Box with Food Scanner,
        Weekly Planner, Chef Bento, smart groceries and a photographed recipe catalog.
      </p>
      <span>Product and commerce</span>
      <p>
        The physical box starts at $49 and includes a personal QR hub plus three
        months of Pro and 210 photo credits, shared by the web and native iPhone app.
      </p>
    </div>
  );
}

function JornalDaIAStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the publication</span>
      <p>
        Jornal da IA is a daily Brazilian newsroom covering artificial intelligence through
        six focused desks: Brazil, Business, Models, Policy, Products and Science.
      </p>
      <span>Editorial system</span>
      <p>
        A live news radar and newsletter handle the daily cycle, while sourced analysis,
        evergreen guides and a Portuguese glossary turn individual stories into a durable library.
      </p>
    </div>
  );
}

function RadarDeIAStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the publication</span>
      <p>
        Radar de IA is a daily Spanish-language newsroom following artificial intelligence
        through World, Business, Models, Policy, Products and Science.
      </p>
      <span>Editorial system</span>
      <p>
        A live headline radar handles breaking developments, while sourced reporting,
        editor recommendations, durable guides and a practical glossary build lasting context.
      </p>
    </div>
  );
}

function NolleiStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the product</span>
      <p>
        Nollei is a made-to-order home store where 15 original visual families move across
        wall art, textiles, lighting, rugs, tabletop pieces and unusual display objects.
      </p>
      <span>Commerce system</span>
      <p>
        The live catalog connects 309 products and their exact variants to Printify production,
        trusted Stripe checkout, US and Canada delivery, and a private customer-art workflow.
      </p>
    </div>
  );
}

function CodeAustralStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the practice</span>
      <p>
        Code Austral brings software engineering, product design and applied AI into one
        remote-first practice working from Argentina and Brazil with teams worldwide.
      </p>
      <span>Work and perspective</span>
      <p>
        Thirty shipped products across web, native iOS, AI, hospitality and learning form
        a living archive shaped by one southern principle: less noise, more useful depth.
      </p>
    </div>
  );
}

function LinkMasStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the product</span>
      <p>
        Link Más gives creators and businesses one branded place for content, products,
        reservations, WhatsApp, social channels and every link their audience needs.
      </p>
      <span>One connected system</span>
      <p>
        Bio pages, measurable short URLs, editable QR codes, live analytics and more than
        150 browser tools share the same account, brand and audience data.
      </p>
    </div>
  );
}

function GenetiqoStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the product</span>
      <p>
        Genetiqo Vault keeps prescribed routines, symptoms, labs, metrics and notes in one
        calm treatment record, then turns a selected date range into a visit-ready summary.
      </p>
      <span>Private by design</span>
      <p>
        The native app works without a Genetiqo account or developer-operated sync backend.
        Visit Briefs are organized on-device, Health imports are user-controlled and PDF or
        CSV records leave the phone only when the person chooses to export them.
      </p>
    </div>
  );
}

function FoodelopersStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the studio</span>
      <p>
        Foodelopers designs and ships food software: multi-restaurant marketplaces,
        delivery systems, POS, kiosks, reservations, loyalty and mobile apps.
      </p>
      <span>Three live demos</span>
      <p>
        Forkit proves the marketplace model, Plateform runs single-brand delivery and POS,
        and Orderly covers tables, kitchen, inventory, staff and restaurant SaaS.
      </p>
      <div className="project-demo-links" aria-label="Foodelopers live demos">
        <a href="https://forkit.foodelopers.com" target="_blank" rel="noreferrer">Forkit <ArrowUpRight size={11} /></a>
        <a href="https://plateform.foodelopers.com" target="_blank" rel="noreferrer">Plateform <ArrowUpRight size={11} /></a>
        <a href="https://orderly.foodelopers.com" target="_blank" rel="noreferrer">Orderly <ArrowUpRight size={11} /></a>
      </div>
    </div>
  );
}

function BunBanditStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the game</span>
      <p>
        Bun Bandit turns every run into a burger order. Collect bun, meat, cheese and
        toppings in the right sequence while dodging kitchen hazards and the crew chasing you.
      </p>
      <span>Native iPhone system</span>
      <p>
        Four districts, seven power-ups, local progression and ten languages run through a
        custom Metal game renderer inside a focused SwiftUI product shell.
      </p>
    </div>
  );
}

function RockClashStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the game</span>
      <p>
        RockClash starts with 50 original songs and a precise four-lane rhythm system.
        Every run records score, accuracy, combo and note-by-note performance.
      </p>
      <span>Create, listen and play</span>
      <p>
        Players can describe a new song, choose genre, language, duration and chart feel,
        then listen, share, download and play the finished track inside the same native app.
      </p>
    </div>
  );
}

function MakoBitsStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the game</span>
      <p>
        MakoBits Merge is a tactical puzzle built around placement, chains, route goals,
        limited moves and character abilities rather than a passive merge board.
      </p>
      <span>A world beyond the board</span>
      <p>
        A 60-Mako Codex connects the puzzle to rooms, care actions, new scenarios and
        collectible progress, so every run feeds a larger native iPhone game.
      </p>
    </div>
  );
}

function JogaIAStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>Four games, one arcade</span>
      <p>
        JogaIA opens directly into Mundo Móvel, Cosmos Merge, Rift Rider and Mecabloom:
        four original 3D games with different movement, scoring and world rules.
      </p>
      <span>Built for immediate play</span>
      <p>
        Every universe runs in the browser with live HUDs, missions, touch controls,
        short sessions and recorded scores—no installer or account between the player and the game.
      </p>
    </div>
  );
}

function TalkMaestroStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the coach</span>
      <p>
        TalkMaestro starts with the room that matters—presentation, interview, pitch,
        sales call, negotiation or hard conversation—then gives the speaker one focused mission.
      </p>
      <span>Speak, fix, redo</span>
      <p>
        Recorded Readouts and realtime Live Roleplay turn each take into a score, one strength,
        precise fixes, a cleaner rewrite and the next drill, while coach memory tracks every rep.
      </p>
    </div>
  );
}

function IAEspacioStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the product</span>
      <p>
        IA Espacio keeps the walls, windows and distribution of a real room recognizable
        while changing its furniture, materials, light and interior style in about 30 seconds.
      </p>
      <span>One complete Studio</span>
      <p>
        More than 50 styles connect with virtual home staging, sketch-to-render, renovation
        controls, image editing, project history and immersive 3D exploration.
      </p>
      <div className="project-demo-links" aria-label="IA Espacio product links">
        <a href="https://iaespacio.com/workspace/" target="_blank" rel="noreferrer">Open Studio <ArrowUpRight size={11} /></a>
      </div>
    </div>
  );
}

function IglesiaJesusStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the product</span>
      <p>
        Iglesia de Jesús connects immediate guided prayer, the complete Bible, daily
        devotionals and a spiritual library in one Spanish-first digital church.
      </p>
      <span>From one visit to a daily rhythm</span>
      <p>
        Three free prayer messages lead into bookmarks, 7, 14 or 30-day plans, community
        access and a membership model that keeps the core experience available to everyone.
      </p>
      <div className="project-demo-links" aria-label="Iglesia de Jesús product links">
        <a href="https://iglesiajesus.com/orar" target="_blank" rel="noreferrer">Pray now <ArrowUpRight size={11} /></a>
        <a href="https://iglesiajesus.com/biblia" target="_blank" rel="noreferrer">Read the Bible <ArrowUpRight size={11} /></a>
      </div>
    </div>
  );
}

function LogoAhoraStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the product</span>
      <p>
        LogoAhora turns a Spanish-language brief into four distinct visual directions so a
        founder can compare ideas before committing to one mark.
      </p>
      <span>From logo to working asset</span>
      <p>
        The selected route can become transparent PNG, native Recraft V4 SVG and precise
        Seedream edits, with commercial use and credit-based access from US$3.
      </p>
    </div>
  );
}

function MatrixAgenciaStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the studio</span>
      <p>
        Matrix Agencia combines creative direction and engineering to build WebGL launches,
        interactive campaign formats and measurable digital experiences for LATAM brands.
      </p>
      <span>From attention to operation</span>
      <p>
        The same system connects 3D experiences with SEO and AI visibility, CRM handoff,
        analytics and automation blueprints for retail, fintech and B2B teams.
      </p>
    </div>
  );
}

function YouAreEarlyStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the platform</span>
      <p>
        You Are Early turns AI adoption data, historical technology curves and practical strategy
        into a guided research experience for founders, operators and creators.
      </p>
      <span>Product and implementation</span>
      <p>
        Next.js and TypeScript structure the research and action system, Framer Motion drives the
        cinematic narrative, and Stripe connects the public sample to a clear paid path.
      </p>
    </div>
  );
}

function MenuCraftersStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the product</span>
      <p>
        MenuCrafters turns a restaurant brief into structured sections, dish names,
        descriptions and starting prices, then keeps every line editable in a live Studio.
      </p>
      <span>One publishing system</span>
      <p>
        The hosted mobile menu, permanent QR, PNG and SVG downloads, table tents and
        print-ready PDF all come from one source that operators can update before service.
      </p>
    </div>
  );
}

function XagodStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the world</span>
      <p>
        XAGOD is a browser-native micro-MMO where a Witness enters a persistent 3D world,
        chooses from nine classes and follows quests through a machine-made mythology.
      </p>
      <span>One game, two ways in</span>
      <p>
        Online play saves characters in a shared WebSocket world. Offline play starts
        instantly, while touch controls make the same combat, map and quest systems playable on mobile.
      </p>
    </div>
  );
}

function PostRaptorStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the product</span>
      <p>
        PostRaptor uses a live website as the creative brief, extracting its logo, palette,
        typography, copy themes and strongest imagery before defining a usable visual direction.
      </p>
      <span>From website to three posts</span>
      <p>
        Every run creates three distinct platform-ready routes with finished art, headline,
        supporting copy and CTA. Ten saved brands can be replayed free in the interactive demo.
      </p>
    </div>
  );
}

function PitchDeckGenieStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>Fundraising product model</span>
      <p>
        PitchDeckGenie normalizes the founder brief into company, customer, traction,
        stage, evidence and ask, then builds a stage-aware argument before visual polish begins.
      </p>
      <span>Application architecture</span>
      <p>
        Next.js and React run onboarding, workspace and editor surfaces. Prisma and PostgreSQL
        persist decks, credits and narrative scores; OpenAI supports generation, Stripe controls
        entitlements and PPTXGenJS delivers editable PowerPoint output.
      </p>
    </div>
  );
}

function VendaKitStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>Production pipeline</span>
      <p>
        VendaKit turns one verified product photo and a structured briefing into a master image,
        marketplace crops, channel-specific copy, downloadable files and a persistent order record.
      </p>
      <span>Application architecture</span>
      <p>
        Next.js 16 and React run the responsive workflow. Prisma and PostgreSQL coordinate orders,
        credits and worker jobs; OpenAI, Sharp and PDF tooling generate deliverables while S3 storage
        and signed Stripe Pix webhooks keep fulfillment recoverable and payment-gated.
      </p>
    </div>
  );
}

function AvatarmaticStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>Conversation product</span>
      <p>
        Avatarmatic turns an agent, presenter, voice and system prompt into a public room that can
        listen, answer, render a talking avatar, retain chat context and embed inside another site.
      </p>
      <span>Application architecture</span>
      <p>
        PHP 8.3 and modular browser JavaScript run the studio and live rooms. MySQL persists agents,
        rooms, chats, logs, recordings and subscriptions; OpenAI drives conversation while Anam and
        D-ID provide avatar sessions and Stripe controls paid workspace access.
      </p>
    </div>
  );
}

function CallTurboStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>Call operations, not one bot</span>
      <p>
        CallTurbo connects inbound answering, outbound campaigns, qualification, routing,
        transfer, SMS and follow-up to the same contacts, outcomes and team workspace.
      </p>
      <span>Application architecture</span>
      <p>
        Express and MySQL coordinate agents, phonebooks, calls, campaigns, CRM records and
        usage. WebSockets carry live state while Twilio, Plivo, SIP, ElevenLabs, Deepgram and
        OpenAI remain configurable providers behind the workflow and Stripe-backed plans.
      </p>
    </div>
  );
}

function PropBotStudioStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the studio</span>
      <p>
        PropBot Studio designs real-estate AI around the full lead journey: answer the first
        question, qualify intent, surface the right property and move the enquiry into action.
      </p>
      <span>Working proof, not mockups</span>
      <p>
        The public chatbot, CRM and voice-agent demos show the three connected surfaces before
        a custom build begins, from multilingual conversation to scoring, follow-up and viewing.
      </p>
    </div>
  );
}

function ManualDelRestauranteStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the platform</span>
      <p>
        Manual del Restaurante routes real symptoms in costs, shifts, service, teams and growth
        into an organized operating system with searchable procedures and guided next steps.
      </p>
      <span>Product and implementation</span>
      <p>
        Next.js and TypeScript power the responsive knowledge paths and passwordless access flow,
        Cloudflare serves the library, and Stripe supports fixed-scope consulting when a restaurant
        needs the operating model adapted to its own context.
      </p>
    </div>
  );
}

function OrcaNaHoraStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the product</span>
      <p>
        OrçaNaHora connects scoping, services, materials, adjustments, Pix details, live totals and
        a client-ready preview in one mobile-first proposal workflow for Brazilian independent workers.
      </p>
      <span>Product and implementation</span>
      <p>
        Next.js and TypeScript drive the PWA editor, PostgreSQL keeps recoverable proposal history,
        Stripe powers optional AI acceleration, and the same structured source feeds review,
        acceptance, WhatsApp follow-up and final export.
      </p>
    </div>
  );
}

function UglyProfitableStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the platform</span>
      <p>
        UglyProfitable organizes 342 necessary, low-glamour businesses into a searchable research
        system with startup ranges, margins, revenue potential, equipment, risks and first-customer paths.
      </p>
      <span>From curiosity to a decision</span>
      <p>
        Free profiles, rankings, state guides and 90 comparisons lead into a $29 operator workspace,
        a $19 resource bundle and optional $49 or $99 human analysis for real local constraints.
      </p>
    </div>
  );
}

function TakeAICourseStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the school</span>
      <p>
        TakeAICourse is an English-language AI school built around practical work: a free essentials
        course, a 30-day flagship path, real projects, workbooks and verifiable certificates.
      </p>
      <span>One connected learning system</span>
      <p>
        The dashboard brings lessons, projects, seven books, 10,071 prompts, Agents, reusable Skills,
        notes and progress together, with individual course access or a $10 monthly all-access plan.
      </p>
    </div>
  );
}

function ZeroToPublicStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the platform</span>
      <p>
        Zero to Public organizes fifty shipped internet products into one evidence-led operating archive,
        connecting launches, costs, revenue, failures and the decisions behind each outcome.
      </p>
      <span>Product and implementation</span>
      <p>
        React and TypeScript power the responsive content system, routed research and sample-report flows.
        Stripe handles commerce while Cloudflare delivers the product and media at the edge.
      </p>
    </div>
  );
}

function WobistroStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>About the AI employee</span>
      <p>
        Wobistro turns daily restaurant evidence into operational memory across cash, inventory,
        shifts, food safety, delivery and maintenance.
      </p>
      <span>Product and implementation</span>
      <p>
        Next.js and PostgreSQL isolate each workspace while Hermes and OpenAI detect exceptions,
        prepare approvals and verify actions. Plans are USD 49/99/199 monthly or USD
        490/990/1990 annually.
      </p>
    </div>
  );
}

function QRLojaStory({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={`project-story${mobile ? " project-story-mobile" : ""}`}>
      <span>One QR, complete local commerce</span>
      <p>
        QRLoja turns a scan into a branded catalog where a customer can buy a product,
        choose pickup or delivery, pay by Pix or card, book a service and return to a
        persistent order or appointment without installing an app.
      </p>
      <span>Application architecture</span>
      <p>
        Next.js 16 and React 19 run storefronts, onboarding and the merchant workspace.
        Prisma and PostgreSQL isolate store, catalog, order and booking state; Better Auth
        provides passwordless access while Stripe Connect webhooks own final payment confirmation.
      </p>
    </div>
  );
}

function HomePage({ index, onOpenProject }: { index: number; onOpenProject: (domain: string) => void }) {
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<(typeof homeProjectFilters)[number]["id"]>("all");
  const filteredProjects = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filter = homeProjectFilters.find((item) => item.id === activeFilter) ?? homeProjectFilters[0];
    return projects.filter((project) => {
      if (!filter.matches(project)) return false;
      if (!normalizedQuery) return true;
      return [
        project.title,
        project.domain,
        project.category,
        project.description,
        ...project.stack,
        ...(project.platforms ?? []),
        ...(project.productTypes ?? []),
      ].join(" ").toLowerCase().includes(normalizedQuery);
    });
  }, [activeFilter, query]);

  return (
    <section className="project-page home-page" data-index={index} aria-labelledby="home-title">
      <article className="project-window home-window">
        <header className="window-bar">
          <div className="traffic-lights" aria-hidden="true"><span /><span /><span /></div>
          <span className="window-domain">gerardo.work</span>
          <button type="button" className="window-open home-browse" data-track="hero_products_click" onClick={() => onOpenProject(projects[0].domain)}>
            Browse work <ArrowRight size={13} aria-hidden />
          </button>
        </header>

        <div className="window-body home-body">
          <section className="home-intro">
            <div className="home-profile">
              <div className="home-profile-intro">
                <figure className="home-portrait">
                  <img
                    src={mediaAssetUrl("/media/home/gerardo-foodphoto.webp")}
                    alt="Gerardo working on a restaurant photo shoot"
                    loading="eager"
                    fetchPriority="high"
                  />
                </figure>
                <div className="home-profile-copy">
                  <div className="home-identity">
                    <h1 id="home-title">Gerardo</h1>
                    <div className="home-eyebrow"><span>Product Builder</span></div>
                  </div>
                  <p className="home-lead">
                    Products for web and iOS, designed, engineered and shipped end to end.
                  </p>
                  <p className="home-bio">
                    A decade running restaurants came first. If you see some restaurant projects
                    in the portfolio, that&apos;s why. The work now is software.
                  </p>
                </div>
              </div>
              <div className="home-facts" aria-label="About Gerardo">
                <div><strong>Web · iOS</strong><span>production applications</span></div>
                <div><strong>Product · systems</strong><span>design, engineering and delivery</span></div>
                <div><strong>Florianópolis</strong><span>Brazil · working internationally</span></div>
              </div>
              <div className="home-press-inline" aria-label="Press coverage">
                <span>FEATURED IN</span>
                <div>
                  {homePress.map((article) => (
                    <a href={article.href} target="_blank" rel="noreferrer" aria-label={`Read ${article.outlet} coverage`} key={article.href}>
                      <img src={mediaAssetUrl(article.logo)} alt={article.outlet} loading="lazy" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <aside className="home-finder" aria-labelledby="project-finder-title">
              <div className="home-finder-heading">
                <h2 id="project-finder-title">Work</h2>
              </div>
              <label className="home-search">
                <Search size={15} aria-hidden />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search product, stack or domain"
                  aria-label="Search projects"
                />
              </label>
              <div className="home-filter-chips" aria-label="Filter projects">
                {homeProjectFilters.map((filter) => (
                  <button
                    type="button"
                    className={activeFilter === filter.id ? "is-active" : ""}
                    onClick={() => setActiveFilter(filter.id)}
                    aria-pressed={activeFilter === filter.id}
                    key={filter.id}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              <div className="home-results" aria-live="polite">
                {filteredProjects.map((project) => (
                  <button type="button" className="home-result" data-track="product_archive_click" data-product-slug={projectSlug(project)} onClick={() => onOpenProject(project.domain)} key={project.domain}>
                    <span>{projectSurfaceLabel(project)}</span>
                    <strong>{project.title}</strong>
                    <small>{project.category} · {project.stack.slice(0, 2).join(" + ")}</small>
                    <ArrowUpRight size={14} aria-hidden />
                  </button>
                ))}
                {!filteredProjects.length && <p className="home-empty">No project matches that combination yet.</p>}
              </div>
            </aside>
          </section>
        </div>
      </article>
    </section>
  );
}

function ProjectPage({ project, index, slideIndex = index, isMediaActive = false }: { project: Project; index: number; slideIndex?: number; isMediaActive?: boolean }) {
  const code = projectCode(project, index);
  const websiteUrl = projectWebsiteUrl(project);
  const visitUrl = project.appStoreUrl ?? websiteUrl;
  const isArtMuseum = project.domain === "artmuseumbrasil.com";
  const isAntesDepois = project.domain === "antesdepois.com";
  const isAIClases = project.domain === "aiclases.com";
  const isAulasDeIA = project.domain === "aulasdeia.com";
  const isRoomRenovation = project.domain === "roomrenovation.ai";
  const isFoodPhoto = project.domain === "foodphoto.ai";
  const isFotoProfissa = project.domain === "fotoprofissa.com";
  const isCursalo = project.domain === "cursalo.com";
  const isEaxy = project.domain === "eaxy.ai";
  const isHuntorizon = project.domain === "huntorizon.com";
  const isMiniBicho = project.domain === "minibicho.com";
  const isSkullSailor = project.domain === "skullsailor.com";
  const isKublaro = project.domain === "kublaro.com";
  const isDevelopArgentina = project.domain === "developargentina.com";
  const isRestaurantArgentina = project.domain === "restaurantargentina.com";
  const isRestaurantMargin = project.domain === "restaurantmargin.com";
  const isEatEasier = project.domain === "eateasier.com";
  const isJornalDaIA = project.domain === "jornaldaia.com";
  const isRadarDeIA = project.domain === "radardeia.com";
  const isNollei = project.domain === "nollei.com";
  const isCodeAustral = project.domain === "codeaustral.com";
  const isLinkMas = project.domain === "linkmas.com";
  const isGenetiqo = project.domain === "genetiqo.com";
  const isFoodelopers = project.domain === "foodelopers.com";
  const isBunBandit = project.domain === "bunbandit.com";
  const isRockClash = project.domain === "rockclash.com";
  const isMakoBits = project.domain === "makobits.com";
  const isJogaIA = project.domain === "jogaia.com";
  const isTalkMaestro = project.domain === "talkmaestro.com";
  const isIAEspacio = project.domain === "iaespacio.com";
  const isIglesiaJesus = project.domain === "iglesiajesus.com";
  const isLogoAhora = project.domain === "logoahora.com";
  const isMatrixAgencia = project.domain === "matrixagencia.com";
  const isYouAreEarly = project.domain === "youareearly.com";
  const isMenuCrafters = project.domain === "menucrafters.com";
  const isXagod = project.domain === "xagod.com";
  const isPostRaptor = project.domain === "postraptor.com";
  const isPitchDeckGenie = project.domain === "pitchdeckgenie.com";
  const isVendaKit = project.domain === "vendakit.com";
  const isAvatarmatic = project.domain === "avatarmatic.com";
  const isCallTurbo = project.domain === "callturbo.com";
  const isPropBotStudio = project.domain === "propbotstudio.com";
  const isManualDelRestaurante = project.domain === "manualdelrestaurante.com";
  const isOrcaNaHora = project.domain === "orcanahora.com";
  const isUglyProfitable = project.domain === "uglyprofitable.com";
  const isTakeAICourse = project.domain === "takeaicourse.com";
  const isZeroToPublic = project.domain === "zerotopublic.com";
  const isWobistro = project.domain === "wobistro.com";
  const isQRLoja = project.domain === "qrloja.com";
  const hasDetailedStory = isAntesDepois || isArtMuseum || isAIClases || isAulasDeIA || isRoomRenovation || isFoodPhoto || isFotoProfissa || isCursalo || isEaxy || isHuntorizon || isMiniBicho || isSkullSailor || isKublaro || isDevelopArgentina || isRestaurantArgentina || isRestaurantMargin || isEatEasier || isJornalDaIA || isRadarDeIA || isNollei || isCodeAustral || isLinkMas || isGenetiqo || isFoodelopers || isBunBandit || isRockClash || isMakoBits || isJogaIA || isTalkMaestro || isIAEspacio || isIglesiaJesus || isLogoAhora || isMatrixAgencia || isYouAreEarly || isMenuCrafters || isXagod || isPostRaptor || isPitchDeckGenie || isVendaKit || isAvatarmatic || isCallTurbo || isPropBotStudio || isManualDelRestaurante || isOrcaNaHora || isUglyProfitable || isTakeAICourse || isZeroToPublic || isWobistro || isQRLoja;

  return (
    <section
      className="project-page"
      id={project.domain}
      data-index={slideIndex}
      aria-labelledby={`project-${index}`}
      style={{ "--project-hue": `${project.hue}` } as CSSProperties}
    >
      <article className="project-window">
        <header className="window-bar">
          <div className="traffic-lights" aria-hidden="true"><span /><span /><span /></div>
          <span className="window-domain">{projectSurfaceLabel(project)}</span>
          {websiteUrl ? (
            <a href={websiteUrl} target="_blank" rel="noreferrer" className="window-open">
              Visit <ArrowUpRight size={13} aria-hidden />
            </a>
          ) : (
            <span className="window-open window-open-status">In progress</span>
          )}
        </header>

        <div className="window-body">
          <div className={`project-copy${hasDetailedStory ? " project-copy-detailed" : ""}`}>
            <div className="project-kicker">
              <span>{code}</span>
              <span>{project.category}</span>
            </div>
            <ProjectTaxonomy project={project} />
            <h1
              id={`project-${index}`}
              style={project.domain === "agrofounder.com" ? { fontSize: "clamp(44px, 4vw, 62px)", overflowWrap: "normal", wordBreak: "normal" } : undefined}
            >
              {project.title}
            </h1>
            <p>{project.description}</p>
            {isAntesDepois && <AntesDepoisStory />}
            {isArtMuseum && <ArtMuseumStory />}
            {isAIClases && <AIClasesStory />}
            {isAulasDeIA && <AulasDeIAStory />}
            {isRoomRenovation && <RoomRenovationStory />}
            {isFoodPhoto && <FoodPhotoStory />}
            {isFotoProfissa && <FotoProfissaStory />}
            {isCursalo && <CursaloStory />}
            {isEaxy && <EaxyStory />}
            {isHuntorizon && <HuntorizonStory />}
            {isMiniBicho && <MiniBichoStory />}
            {isSkullSailor && <SkullSailorStory />}
            {isKublaro && <KublaroStory />}
            {isDevelopArgentina && <DevelopArgentinaStory />}
            {isRestaurantArgentina && <RestaurantArgentinaStory />}
            {isRestaurantMargin && <RestaurantMarginStory />}
            {isEatEasier && <EatEasierStory />}
            {isJornalDaIA && <JornalDaIAStory />}
            {isRadarDeIA && <RadarDeIAStory />}
            {isNollei && <NolleiStory />}
            {isCodeAustral && <CodeAustralStory />}
            {isLinkMas && <LinkMasStory />}
            {isGenetiqo && <GenetiqoStory />}
            {isFoodelopers && <FoodelopersStory />}
            {isBunBandit && <BunBanditStory />}
            {isRockClash && <RockClashStory />}
            {isMakoBits && <MakoBitsStory />}
            {isJogaIA && <JogaIAStory />}
            {isTalkMaestro && <TalkMaestroStory />}
            {isIAEspacio && <IAEspacioStory />}
            {isIglesiaJesus && <IglesiaJesusStory />}
            {isLogoAhora && <LogoAhoraStory />}
            {isMatrixAgencia && <MatrixAgenciaStory />}
            {isYouAreEarly && <YouAreEarlyStory />}
            {isMenuCrafters && <MenuCraftersStory />}
            {isXagod && <XagodStory />}
            {isPostRaptor && <PostRaptorStory />}
            {isPitchDeckGenie && <PitchDeckGenieStory />}
            {isVendaKit && <VendaKitStory />}
            {isAvatarmatic && <AvatarmaticStory />}
            {isCallTurbo && <CallTurboStory />}
            {isPropBotStudio && <PropBotStudioStory />}
            {isManualDelRestaurante && <ManualDelRestauranteStory />}
            {isOrcaNaHora && <OrcaNaHoraStory />}
            {isUglyProfitable && <UglyProfitableStory />}
            {isTakeAICourse && <TakeAICourseStory />}
            {isZeroToPublic && <ZeroToPublicStory />}
            {isWobistro && <WobistroStory />}
            {isQRLoja && <QRLojaStory />}
            <div className="project-links">
              {visitUrl && (
                <a href={visitUrl} target="_blank" rel="noreferrer" className="project-link">
                  Visit Project <ArrowUpRight size={15} aria-hidden />
                </a>
              )}
            </div>
            <div className="project-letter" aria-hidden="true">{project.domain[0].toUpperCase()}</div>
          </div>

          <div className="project-visuals">
            <div className="visual-label"><span>01</span> Media stack</div>
            {isMediaActive
              ? <MediaDeck project={project} index={index} />
              : <div className="media-deck" aria-hidden="true" />}
            <div className="stack-heading"><span>02</span> Built with</div>
            <StackGrid stack={project.stack} />
            {isAntesDepois && <AntesDepoisStory mobile />}
            {isArtMuseum && <ArtMuseumStory mobile />}
            {isAIClases && <AIClasesStory mobile />}
            {isAulasDeIA && <AulasDeIAStory mobile />}
            {isRoomRenovation && <RoomRenovationStory mobile />}
            {isFoodPhoto && <FoodPhotoStory mobile />}
            {isFotoProfissa && <FotoProfissaStory mobile />}
            {isCursalo && <CursaloStory mobile />}
            {isEaxy && <EaxyStory mobile />}
            {isHuntorizon && <HuntorizonStory mobile />}
            {isMiniBicho && <MiniBichoStory mobile />}
            {isSkullSailor && <SkullSailorStory mobile />}
            {isKublaro && <KublaroStory mobile />}
            {isDevelopArgentina && <DevelopArgentinaStory mobile />}
            {isRestaurantArgentina && <RestaurantArgentinaStory mobile />}
            {isRestaurantMargin && <RestaurantMarginStory mobile />}
            {isEatEasier && <EatEasierStory mobile />}
            {isJornalDaIA && <JornalDaIAStory mobile />}
            {isRadarDeIA && <RadarDeIAStory mobile />}
            {isNollei && <NolleiStory mobile />}
            {isCodeAustral && <CodeAustralStory mobile />}
            {isLinkMas && <LinkMasStory mobile />}
            {isGenetiqo && <GenetiqoStory mobile />}
            {isFoodelopers && <FoodelopersStory mobile />}
            {isBunBandit && <BunBanditStory mobile />}
            {isRockClash && <RockClashStory mobile />}
            {isMakoBits && <MakoBitsStory mobile />}
            {isJogaIA && <JogaIAStory mobile />}
            {isTalkMaestro && <TalkMaestroStory mobile />}
            {isIAEspacio && <IAEspacioStory mobile />}
            {isIglesiaJesus && <IglesiaJesusStory mobile />}
            {isLogoAhora && <LogoAhoraStory mobile />}
            {isMatrixAgencia && <MatrixAgenciaStory mobile />}
            {isYouAreEarly && <YouAreEarlyStory mobile />}
            {isMenuCrafters && <MenuCraftersStory mobile />}
            {isXagod && <XagodStory mobile />}
            {isPostRaptor && <PostRaptorStory mobile />}
            {isPitchDeckGenie && <PitchDeckGenieStory mobile />}
            {isVendaKit && <VendaKitStory mobile />}
            {isAvatarmatic && <AvatarmaticStory mobile />}
            {isCallTurbo && <CallTurboStory mobile />}
            {isPropBotStudio && <PropBotStudioStory mobile />}
            {isManualDelRestaurante && <ManualDelRestauranteStory mobile />}
            {isOrcaNaHora && <OrcaNaHoraStory mobile />}
            {isUglyProfitable && <UglyProfitableStory mobile />}
            {isTakeAICourse && <TakeAICourseStory mobile />}
            {isZeroToPublic && <ZeroToPublicStory mobile />}
            {isWobistro && <WobistroStory mobile />}
            {isQRLoja && <QRLojaStory mobile />}
          </div>
        </div>
      </article>
    </section>
  );
}

function destinationLinks(project: Project) {
  if (project.destinations?.length) return project.destinations;
  const links: NonNullable<Project["destinations"]> = [];
  const websiteUrl = projectWebsiteUrl(project);
  if (websiteUrl) links.push({ label: "Open live website", href: websiteUrl, kind: "web" });
  if (project.appStoreUrl) links.push({ label: "Download for iOS", href: project.appStoreUrl, kind: "ios" });
  return links;
}

function caseMediaPoster(src: string) {
  return src.replace(/\.(mp4|webm)(\?.*)?$/i, ".jpg$2");
}

function CaseStudyPage({ project }: { project: Project }) {
  const study = getCaseStudy(project);
  const taxonomy = projectTaxonomy(project);
  const destinations = destinationLinks(project);
  const navDestinations = project.domain === "restaurantmargin.com" ? destinations.slice(0, 1) : destinations;
  const allCaseMediaLandscape = study.media.length > 0 && study.media.every((media) => media.format !== "portrait");
  const allCaseMediaPortrait = study.media.length > 0 && study.media.every((media) => media.format === "portrait");
  const fourCaseMediaLandscape = study.media.length === 4 && allCaseMediaLandscape;
  const usesDistinctSceneStarts = ["eaxy.ai", "huntorizon.com", "skullsailor.com", "minibicho.com"].includes(project.domain);

  useEffect(() => {
    document.title = `${project.title} — Case Study — Gerardo.Work`;
    window.scrollTo(0, 0);
  }, [project]);

  return (
    <main className="case-study-shell" data-project={project.domain}>
      <header className="case-study-nav">
        <a href={`#${project.domain}`} className="case-back"><ArrowLeft size={15} /> All projects</a>
        <a href="#aulasdeia.com" className="case-wordmark">gerardo<span>.work</span></a>
        <div className="case-nav-destinations">
          {navDestinations.map((destination) => (
            <a href={destination.href} target="_blank" rel="noreferrer" key={destination.href}>
              {["foodelopers.com", "iaespacio.com"].includes(project.domain)
                ? destination.label.replace(/^(Open|Try)\s+/, "")
                : destination.kind}
              <ArrowUpRight size={12} />
            </a>
          ))}
        </div>
      </header>

      <section className="case-hero">
        <div className="case-hero-copy">
          <div className="case-eyebrow"><span>{project.category}</span><span>{projectSurfaceLabel(project)}</span></div>
          <h1>{project.title}</h1>
          <p className="case-thesis">{study.thesis}</p>
          <div className="case-taxonomy">
            <span>{taxonomy.platforms.join(" / ")}</span>
            <span>{taxonomy.productTypes.join(" / ")}</span>
            <span>{taxonomy.languages.join(" / ")}</span>
          </div>
          <div className="case-destination-grid">
            {destinations.map((destination) => (
              <a href={destination.href} target="_blank" rel="noreferrer" key={destination.href}>
                <small>{destination.kind}</small>
                <strong>{destination.label}</strong>
                <ArrowUpRight size={16} />
              </a>
            ))}
          </div>
        </div>
        <div className={`case-hero-media${allCaseMediaLandscape ? " is-all-landscape" : ""}${allCaseMediaPortrait ? " is-all-portrait" : ""}`} aria-label="Project media preview">
          {study.media.slice(0, 3).map((media, index) => (
            <figure className={`${media.format === "portrait" ? "is-portrait" : ""} hero-media-${index + 1}`} key={media.label}>
              <img src={mediaAssetUrl(media.type === "video" ? caseMediaPoster(media.src) : media.src)} alt={media.label} />
            </figure>
          ))}
          {!study.media.length && <div className="case-hero-placeholder">{project.domain[0].toUpperCase()}</div>}
        </div>
      </section>

      <section className="case-intro case-section">
        <div className="case-section-label">01 · Context</div>
        <div className="case-intro-grid">
          <div><h2>Where it started</h2><p>{study.origin}</p></div>
          <div><h2>The product challenge</h2><p>{study.challenge}</p></div>
        </div>
      </section>

      <section className="case-process case-section">
        <div className="case-section-label">02 · Process</div>
        <div className="case-process-grid">
          {study.process.map((phase) => (
            <article key={phase.step}>
              <span>{phase.step}</span>
              <h3>{phase.title}</h3>
              <p>{phase.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="case-media case-section">
        <div className="case-section-label">03 · Product media</div>
        {study.media.length ? (
          <div className={`case-media-grid${allCaseMediaPortrait ? " is-all-portrait" : ""}${fourCaseMediaLandscape ? " is-four-landscape" : ""}`}>
            {study.media.map((media, mediaIndex) => (
              <figure className={media.format === "portrait" ? "is-portrait" : ""} key={media.label}>
                {media.type === "video" ? (
                  <VisibleLoopVideo
                    src={media.src}
                    poster={caseMediaPoster(media.src)}
                    label={media.label}
                    startFraction={usesDistinctSceneStarts ? 0 : mediaPhaseOffsets[mediaIndex % mediaPhaseOffsets.length]}
                  />
                ) : <img src={mediaAssetUrl(media.src)} alt={media.label} />}
                <figcaption>{media.label}</figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <div className="case-media-pending"><span>{project.domain[0].toUpperCase()}</span><p>Product archive being organized.</p></div>
        )}
      </section>

      <section className="case-assets case-section">
        <div className="case-section-label">04 · Asset library</div>
        {study.assetGallery.length > 0 && (
          <div className={`case-asset-gallery${["antesdepois.com", "cursalo.com", "fotoprofissa.com", "genetiqo.com", "foodelopers.com", "bunbandit.com", "rockclash.com", "makobits.com", "jogaia.com", "talkmaestro.com", "iaespacio.com", "iglesiajesus.com", "menucrafters.com", "postraptor.com", "propbotstudio.com", "manualdelrestaurante.com", "orcanahora.com", "uglyprofitable.com", "takeaicourse.com", "zerotopublic.com", "restaurantmargin.com", "vendakit.com", "avatarmatic.com", "callturbo.com", "qrloja.com"].includes(project.domain) ? " is-natural-gallery" : ""}`}>
            {study.assetGallery.map((asset, index) => (
              <figure className={`asset-gallery-item asset-gallery-item-${index + 1}`} key={asset.src}>
                <img src={mediaAssetUrl(asset.src)} alt={asset.label} />
                <figcaption><strong>{asset.label}</strong><span>{asset.meta}</span></figcaption>
              </figure>
            ))}
          </div>
        )}
        <div className="case-assets-grid">
          {study.assetNotes.map((asset, index) => (
            <article key={asset.label}><span>{String(index + 1).padStart(2, "0")}</span><h3>{asset.label}</h3><p>{asset.value}</p></article>
          ))}
        </div>
      </section>

      <section className="case-system case-section">
        <div className="case-section-label">05 · System</div>
        <h2>Built as a production<br />system.</h2>
        <p className="case-system-summary">
          {project.stack.slice(0, 2).join(" and ")} define the primary application layer. {" "}
          {project.stack.slice(2).join(" and ")} support the domain services, persistence, commerce
          or delivery required to operate the {project.platforms?.join(" and ") ?? "product"} system in production.
        </p>
        <StackGrid stack={project.stack} />
      </section>

      <footer className="case-footer">
        <a href={`#${project.domain}`}><ArrowLeft size={15} /> Back to portfolio</a>
        <div>{destinations.map((destination) => <a href={destination.href} target="_blank" rel="noreferrer" key={destination.href}>{destination.label}<ArrowUpRight size={13} /></a>)}</div>
      </footer>
    </main>
  );
}

function normalizeProjectDomain(domain: string) {
  return domain === "journaldaia.com" ? "jornaldaia.com" : domain;
}

function PortfolioView() {
  const trackRef = useRef<HTMLDivElement>(null);
  const wheelLockRef = useRef(false);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const alphabetCarouselRef = useRef<HTMLDivElement>(null);
  const alphabetGestureRef = useRef<{ pointerId: number; startX: number; startY: number; dragging: boolean } | null>(null);
  const initialDomain = typeof window === "undefined" ? "" : normalizeProjectDomain(decodeURIComponent(window.location.hash.slice(1)));
  const requestedProjectIndex = projects.findIndex((project) => project.domain === initialDomain);
  const initialSlideIndexRef = useRef(requestedProjectIndex >= 0 ? requestedProjectIndex + 1 : 0);
  const [currentIndex, setCurrentIndex] = useState(initialSlideIndexRef.current);
  const initialLetter = initialSlideIndexRef.current === 0 ? "home" : projects[initialSlideIndexRef.current - 1].domain[0].toUpperCase();
  const [alphabetOpen, setAlphabetOpen] = useState(false);
  const [alphabetSelection, setAlphabetSelection] = useState(initialLetter);

  const letterIndexes = useMemo(() => {
    return Object.fromEntries(letters.map((letter) => [letter, projects.findIndex((project) => project.domain.startsWith(letter.toLowerCase())) + 1]));
  }, []);

  const goTo = useCallback((index: number, behavior: ScrollBehavior = "smooth") => {
    const track = trackRef.current;
    if (!track) return;
    const page = track.querySelector<HTMLElement>(`[data-index="${index}"]`);
    page?.querySelector<HTMLElement>(".window-body")?.scrollTo({ top: 0, behavior: "auto" });
    const targetLeft = index * track.clientWidth;
    if (behavior === "auto") {
      const previousScrollBehavior = track.style.scrollBehavior;
      track.style.scrollBehavior = "auto";
      track.scrollLeft = targetLeft;
      window.requestAnimationFrame(() => {
        track.style.scrollBehavior = previousScrollBehavior;
      });
      return;
    }
    track.scrollTo({ left: targetLeft, top: 0, behavior });
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const onWheel = (event: WheelEvent) => {
      const targetElement = event.target as Element | null;
      const targetPage = targetElement?.closest<HTMLElement>(".project-page");
      const detailedCopy = targetElement?.closest<HTMLElement>(".project-copy-detailed");
      const windowBody = targetElement?.closest<HTMLElement>(".window-body")
        ?? targetPage?.querySelector<HTMLElement>(".window-body");
      const verticalPanel = detailedCopy && detailedCopy.scrollHeight > detailedCopy.clientHeight + 2
        ? detailedCopy
        : windowBody;
      if (window.matchMedia("(max-width: 780px), (pointer: coarse)").matches) {
        return;
      }
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX) || Math.abs(event.deltaY) < 6) return;

      if (verticalPanel && verticalPanel.scrollHeight > verticalPanel.clientHeight + 2) {
        const movingDown = event.deltaY > 0;
        const atTop = verticalPanel.scrollTop <= 1;
        const atBottom = verticalPanel.scrollTop + verticalPanel.clientHeight >= verticalPanel.scrollHeight - 1;
        const canContinueVertically = movingDown ? !atBottom : !atTop;
        if (canContinueVertically) return;
      }

      event.preventDefault();
      if (wheelLockRef.current) return;

      const direction = event.deltaY > 0 ? 1 : -1;
      const nextIndex = Math.max(0, Math.min(projects.length, currentIndex + direction));
      if (nextIndex === currentIndex) return;

      wheelLockRef.current = true;
      goTo(nextIndex);
      window.setTimeout(() => {
        wheelLockRef.current = false;
      }, 720);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") goTo(Math.min(projects.length, currentIndex + 1));
      if (event.key === "ArrowLeft") goTo(Math.max(0, currentIndex - 1));
    };

    const onTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) return;
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    };

    const onTouchEnd = (event: TouchEvent) => {
      const start = touchStartRef.current;
      const touch = event.changedTouches[0];
      touchStartRef.current = null;
      if (!start || !touch) return;

      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;
      if (Math.abs(deltaX) < 52 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;

      const direction = deltaX < 0 ? 1 : -1;
      goTo(Math.max(0, Math.min(projects.length, currentIndex + direction)));
    };

    track.addEventListener("wheel", onWheel, { passive: false });
    track.addEventListener("touchstart", onTouchStart, { passive: true });
    track.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      track.removeEventListener("wheel", onWheel);
      track.removeEventListener("touchstart", onTouchStart);
      track.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [currentIndex, goTo]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    track.querySelectorAll<HTMLElement>(".window-body").forEach((body) => body.scrollTop = 0);
    goTo(initialSlideIndexRef.current, "auto");

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const nextIndex = Number((visible.target as HTMLElement).dataset.index);
        setCurrentIndex(nextIndex);
        if (nextIndex === 0) {
          window.history.replaceState(null, "", "#home");
          document.title = "Gerardo — Product Builder";
          return;
        }
        const project = projects[nextIndex - 1];
        window.history.replaceState(null, "", `#${project.domain}`);
        document.title = `${project.title} — Gerardo.Work`;
      },
      { root: track, threshold: [0.55, 0.8] },
    );

    track.querySelectorAll("[data-index]").forEach((page) => observer.observe(page));
    return () => observer.disconnect();
  }, [goTo]);

  const currentProject = currentIndex === 0 ? null : projects[currentIndex - 1];
  const currentLetter = currentProject ? currentProject.domain[0].toUpperCase() : "home";

  const revealAlphabetLetter = useCallback((letter: string) => {
    setAlphabetSelection(letter);
    window.requestAnimationFrame(() => {
      alphabetCarouselRef.current
        ?.querySelector<HTMLElement>(`[data-letter="${letter}"]`)
        ?.scrollIntoView({ behavior: "auto", inline: "center", block: "nearest" });
    });
  }, []);

  useEffect(() => {
    if (!alphabetOpen) return;
    revealAlphabetLetter(currentLetter);
  }, [alphabetOpen, currentLetter, revealAlphabetLetter]);

  const selectAlphabetLetter = useCallback((letter: string) => {
    if (letter === "home") {
      goTo(0);
      setAlphabetOpen(false);
      return;
    }
    const index = letterIndexes[letter];
    if (index >= 0) goTo(index, "auto");
    setAlphabetOpen(false);
  }, [goTo, letterIndexes]);

  const alphabetLetterFromPointer = useCallback((clientX: number) => {
    const normalized = Math.min(0.999, Math.max(0, clientX / window.innerWidth));
    return alphabetEntries[Math.floor(normalized * alphabetEntries.length)];
  }, []);

  const onAlphabetPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!window.matchMedia("(max-width: 780px), (pointer: coarse)").matches) return;
    alphabetGestureRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      dragging: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setAlphabetOpen(true);
    revealAlphabetLetter(currentLetter);
  };

  const onAlphabetPointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const gesture = alphabetGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    const distance = Math.hypot(event.clientX - gesture.startX, event.clientY - gesture.startY);
    if (distance > 8) gesture.dragging = true;
    if (!gesture.dragging) return;
    event.preventDefault();
    revealAlphabetLetter(alphabetLetterFromPointer(event.clientX));
  };

  const finishAlphabetGesture = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const gesture = alphabetGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    alphabetGestureRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (gesture.dragging) selectAlphabetLetter(alphabetLetterFromPointer(event.clientX));
  };

  return (
    <div className="portfolio-shell">
      <header className="global-bar">
        <button className="wordmark" onClick={() => goTo(0)} aria-label="Go to first project">
          gerardo<span>.work</span>
        </button>
        <div className="global-center">
          <span className="status-dot" /> Product Builder
        </div>
      </header>

      <main className="horizontal-track" ref={trackRef} aria-label="A to Z project portfolio">
        <HomePage index={0} onOpenProject={(domain) => goTo(projects.findIndex((project) => project.domain === domain) + 1, "auto")} />
        {projects.map((project, index) => (
          <ProjectPage
            project={project}
            index={index}
            slideIndex={index + 1}
            isMediaActive={Math.abs(currentIndex - (index + 1)) <= 1}
            key={project.domain}
          />
        ))}
      </main>

      <div className={`mobile-alphabet-picker${alphabetOpen ? " is-open" : ""}`} aria-hidden={!alphabetOpen}>
        <div className="alphabet-carousel" ref={alphabetCarouselRef}>
          {alphabetEntries.map((letter) => (
            <button
              type="button"
              data-letter={letter}
              className={`${letter === currentLetter ? "is-current " : ""}${letter === alphabetSelection ? "is-selected" : ""}`}
              onClick={() => selectAlphabetLetter(letter)}
              aria-label={letter === "home" ? "Go to home" : `Go to projects beginning with ${letter}`}
              key={letter}
            >
              {letter === "home" ? <HomeIcon size={14} aria-hidden /> : letter}
            </button>
          ))}
        </div>
      </div>

      <nav className="alphabet-dock" aria-label="Project alphabet">
        <button
          type="button"
          className="dock-copy"
          aria-expanded={alphabetOpen}
          aria-label="Open project alphabet"
          onPointerDown={onAlphabetPointerDown}
          onPointerMove={onAlphabetPointerMove}
          onPointerUp={finishAlphabetGesture}
          onPointerCancel={finishAlphabetGesture}
        >
          <strong>{currentProject ? currentLetter : <HomeIcon size={15} aria-hidden />}</strong><span>{currentProject?.domain ?? "gerardo.work"}</span>
        </button>
        <div className="dock-letters">
          <button
            className={currentIndex === 0 ? "active" : ""}
            onClick={() => goTo(0)}
            aria-label="Go to home"
          >
            <HomeIcon size={13} aria-hidden />
          </button>
          {letters.map((letter) => (
            <button
              className={letter === currentLetter ? "active" : ""}
              onClick={() => goTo(letterIndexes[letter])}
              aria-label={`Go to projects beginning with ${letter}`}
              key={letter}
            >
              {letter}
            </button>
          ))}
        </div>
        <div className="dock-instruction">
          <button
            type="button"
            onClick={() => goTo(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
            aria-label="Go to previous project"
          >
            <span aria-hidden>←</span> Previous
          </button>
          <button
            type="button"
            onClick={() => goTo(Math.min(projects.length, currentIndex + 1))}
            disabled={currentIndex === projects.length}
            aria-label="Go to next project"
          >
            Next <span aria-hidden>→</span>
          </button>
        </div>
      </nav>

      <div className="progress-line" aria-hidden="true">
        <span style={{ width: `${((currentIndex + 1) / (projects.length + 1)) * 100}%` }} />
      </div>
    </div>
  );
}

function caseDomainFromHash() {
  const hash = decodeURIComponent(window.location.hash.slice(1));
  return hash.startsWith("case/") ? normalizeProjectDomain(hash.slice(5)) : "";
}

export default function App() {
  const [caseDomain, setCaseDomain] = useState(caseDomainFromHash);

  useEffect(() => {
    const syncRoute = () => setCaseDomain(caseDomainFromHash());
    window.addEventListener("hashchange", syncRoute);
    return () => window.removeEventListener("hashchange", syncRoute);
  }, []);

  const caseProject = projects.find((project) => project.domain === caseDomain);
  if (caseProject) return <CaseStudyPage project={caseProject} />;
  return <PortfolioView key={window.location.hash} />;
}
